import { Tabs, Spin, Modal, Input, Button, InputNumber, DatePicker, Select, message, Tag } from "antd";
import { FC, useMemo, useState, Fragment, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, CheckCircle, Plus, Trash2, Package } from "lucide-react";
import { useReactToPrint } from "react-to-print";
import { useDetailBookingStore } from "../detail-booking.store";
import { ServiceType } from "~/utils/constants";
import { capitalize, getClientName } from "~/utils";
import dayjs from "dayjs";
import { useGetStaffRoleByName } from "~/services/common";
import { addOnServicesApi } from "~/services/add-on-services/add-on-services.api";
import type { AddOnServiceDto } from "~/services/add-on-services/add-on-services.types";
import { createInvoice, getInvoice, getInvoicesForBooking } from "~/services/billing/billing.api";
import { bookingService } from "~/services/booking/booking.services";
import { useGetAdminCharges } from "~/services/admin-charge";
import { useGetSupplies } from "~/services/supply/supply.queries";
import { useGetRateTypeDiscounts } from "~/services/rate-type-discounts";
import { useGetLocations } from "~/services/location";
import { InvoicePrintContent } from "./invoice-print-content";
import { EstimateSidebar, EstimateVersion } from "./estimate-sidebar";
import { ProfitLoss } from "./profit-loss";
import { useGetTasks } from "~/services/task";
import { TaskStatus } from "~/utils/constants";
import { v4 as uuidv4 } from "uuid";

// Format currency helper function
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined) return "$0.00";
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

// Calculate travel time in hours - only if vehicle is selected
const getTravelTimeHours = (service: any): number => {
  // No vehicle = no travel time
  if (!service.vessel) {
    return 0;
  }

  // Check multiple possible sources for travel time
  // service.travelTime is from estimate form (already in hours or fractional)
  // location/address travelTime is typically in minutes
  if (service.travelTime !== undefined && service.travelTime !== null) {
    // Estimate form saves as hours (e.g., 0.5 = 30 min)
    return Number(service.travelTime);
  }

  const travelTimeMinutes =
    service.location?.travelTime ||
    service.client?.shippingAddresses?.[0]?.travelTime ||
    30; // Default 30 minutes
  return travelTimeMinutes / 60; // Convert to hours
};

// Get duration in hours
const getDurationHours = (service: any): number => {
  if (service.timeIn && service.timeOut) {
    const timeIn = dayjs(service.timeIn);
    const timeOut = dayjs(service.timeOut);
    return timeOut.diff(timeIn, "hour", true);
  }
  return Number(service.duration || 1); // Default 1 hour
};

type Props = {};

// Interface for service overrides
interface ServiceOverride {
  serviceId: string;
  customVariables?: {
    staffCount?: number;
    staffRate?: number;
    vesselRate?: number;
    hours?: number;
    cubicFeet?: number;
  };
  discountPercentage?: number;
  customTotal?: number;
}

export const Billing: FC<Props> = () => {
  const { booking, setBooking } = useDetailBookingStore();
  const queryClient = useQueryClient();
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [serviceOverrides, setServiceOverrides] = useState<Map<string, ServiceOverride>>(new Map());
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [currentServiceOverride, setCurrentServiceOverride] = useState<any>(null);
  const [requestedDeposit, setRequestedDeposit] = useState<number>(0);
  const [dueDate, setDueDate] = useState<string>('');
  const [netTerms, setNetTerms] = useState<number>(-1); // -1 = Due Before Delivery (default for estimates)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [groupBy, setGroupBy] = useState<'none' | 'type' | 'date'>('date');
  const invoicePrintRef = useRef<HTMLDivElement>(null);
  const estimatePrintRef = useRef<HTMLDivElement>(null);
  const existingInvoicePrintRef = useRef<HTMLDivElement>(null);
  const [saveInvoiceModalVisible, setSaveInvoiceModalVisible] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState<{ id: string; invoiceNumber: string } | null>(null);
  const [invoiceToPrint, setInvoiceToPrint] = useState<any>(null);

  // Storage estimate state
  const [includeStorage, setIncludeStorage] = useState(false);
  const [storageExpanded, setStorageExpanded] = useState(false);
  const [selectedStorageLocationId, setSelectedStorageLocationId] = useState<string | null>(null);
  const [storageMinCubicFeet, setStorageMinCubicFeet] = useState<number>(100);

  // Admin charges state
  const [selectedAdminCharges, setSelectedAdminCharges] = useState<Map<string, { chargeId: string; quantity: number; }>>(new Map());

  // Additional charges state (custom line items + supplies)
  type AdditionalCharge = {
    id: string;
    type: 'custom' | 'supply';
    description: string;
    notes?: string; // Additional details/description for the charge
    date?: string;
    quantity: number;
    rate: number;
    supplyId?: string;
  };
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharge[]>([]);

  // Overtime hours state for expo positions - Map<"serviceId_positionIndex", number of OT hours>
  // Example: If position is 11 hours total and 3 are OT, store 3 (8 regular + 3 OT at 1.5x)
  const [expoOvertimeHours, setExpoOvertimeHours] = useState<Map<string, number>>(new Map());

  // Version history state
  const [estimateVersions, setEstimateVersions] = useState<EstimateVersion[]>([]);
  const [isSavingEstimate, setIsSavingEstimate] = useState(false);

  // Fetch admin charges
  const { data: adminChargesData } = useGetAdminCharges({ params: {} });
  const adminCharges = useMemo(() => adminChargesData?.items || [], [adminChargesData]);

  // Fetch supplies for additional charges
  const { data: suppliesData } = useGetSupplies({ pageSize: 500 });
  const supplies = useMemo(() => suppliesData?.items || [], [suppliesData]);

  // Fetch rate type discounts
  const { data: rateTypeDiscounts } = useGetRateTypeDiscounts();

  // Fetch billable tasks for this booking
  const { data: tasksData } = useGetTasks({
    jobTicketId: booking?.id,
    isArchived: false,
  });


  // Fetch existing invoices for this booking
  const { data: existingInvoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['booking-invoices', booking?.id],
    queryFn: () => booking?.id ? getInvoicesForBooking(booking.id) : [],
    enabled: !!booking?.id,
  });

  // Load persisted invoice config when booking changes
  useEffect(() => {
    if (booking?.invoiceConfig) {
      const config = booking.invoiceConfig;
      setSelectedServices(new Set(config.selectedServices || []));
      setRequestedDeposit(config.requestedDeposit || 0);
      setDueDate(config.dueDate || '');
      setNetTerms(config.netTerms ?? -1); // Default to "Due Before Delivery"

      if (config.serviceOverrides && config.serviceOverrides.length > 0) {
        const overrideMap = new Map();
        config.serviceOverrides.forEach((override: any) => {
          if (override.serviceId) {
            overrideMap.set(override.serviceId, override);
          }
        });
        setServiceOverrides(overrideMap);
      } else {
        setServiceOverrides(new Map());
      }

      // Load admin charges
      if (config.adminCharges) {
        const chargesMap = new Map();
        config.adminCharges.forEach((charge: any) => {
          chargesMap.set(charge.chargeId, { chargeId: charge.chargeId, quantity: charge.quantity });
        });
        setSelectedAdminCharges(chargesMap);
      }

      // Load additional charges
      if ((config as any).additionalCharges && Array.isArray((config as any).additionalCharges)) {
        setAdditionalCharges((config as any).additionalCharges);
      } else {
        setAdditionalCharges([]);
      }

      // Load storage config
      setIncludeStorage(!!(config as any).includeStorage);
      setSelectedStorageLocationId((config as any).selectedStorageLocationId || null);
      setStorageMinCubicFeet((config as any).storageMinCubicFeet ?? 100);

      // Load expo overtime hours
      if ((config as any).expoOvertimeHours && Array.isArray((config as any).expoOvertimeHours)) {
        const otMap = new Map<string, number>();
        (config as any).expoOvertimeHours.forEach(([key, value]: [string, number]) => {
          otMap.set(key, value);
        });
        setExpoOvertimeHours(otMap);
      } else if ((config as any).expoOvertimePositions && Array.isArray((config as any).expoOvertimePositions)) {
        // Legacy support: convert old boolean format to new hours format (assume full duration was OT)
        setExpoOvertimeHours(new Map());
      } else {
        setExpoOvertimeHours(new Map());
      }

      // Reset unsaved changes flag when loading config
      setHasUnsavedChanges(false);
    }
  }, [booking?.id]);

  // Load version history from new table (with fallback to JSONB)
  useEffect(() => {
    const loadVersions = async () => {
      if (!booking?.id) return;

      try {
        // Try to load from new table first
        const versions = await bookingService.getEstimateVersions(booking.id);
        
        if (versions && versions.length > 0) {
          // Transform database versions to EstimateVersion format
          const transformedVersions: EstimateVersion[] = versions.map((v: any) => ({
            id: v.id,
            savedAt: v.savedAt,
            total: Number(v.totalAmount),
            notes: v.notes || undefined,
            config: v.configSnapshot,
            versionNumber: v.versionNumber,
            savedBy: v.savedBy ? {
              id: v.savedBy.id,
              firstName: v.savedBy.firstName,
              lastName: v.savedBy.lastName,
            } : undefined,
            changeSummary: v.changeSummary,
          }));
          setEstimateVersions(transformedVersions);
        } else {
          // Fallback to JSONB if no versions in new table
          const config = booking.invoiceConfig;
          if ((config as any)?.versions && Array.isArray((config as any).versions)) {
            setEstimateVersions((config as any).versions);
          } else {
            setEstimateVersions([]);
          }
        }
      } catch (error) {
        console.error('Error loading estimate versions:', error);
        // Fallback to JSONB on error
        const config = booking.invoiceConfig;
        if ((config as any)?.versions && Array.isArray((config as any).versions)) {
          setEstimateVersions((config as any).versions);
        } else {
          setEstimateVersions([]);
        }
      }
    };

    loadVersions();
  }, [booking?.id]);

  // Track changes to detect unsaved modifications
  useEffect(() => {
    if (!booking?.invoiceConfig) {
      // If there's no saved config but we have selections, mark as unsaved
      if (selectedServices.size > 0 || serviceOverrides.size > 0 || selectedAdminCharges.size > 0 || additionalCharges.length > 0 || includeStorage) {
        setHasUnsavedChanges(true);
      } else {
        setHasUnsavedChanges(false);
      }
      return;
    }

    const config = booking.invoiceConfig;

    // Check if current state differs from saved config
    const selectedServicesChanged =
      selectedServices.size !== (config.selectedServices || []).length ||
      !Array.from(selectedServices).every(id => config.selectedServices?.includes(id));

    const overridesChanged =
      serviceOverrides.size !== (config.serviceOverrides || []).length;

    const adminChargesChanged =
      selectedAdminCharges.size !== (config.adminCharges || []).length;

    const additionalChargesChanged =
      additionalCharges.length !== ((config as any).additionalCharges || []).length;

    const depositChanged = requestedDeposit !== (config.requestedDeposit || 0);
    const dueDateChanged = dueDate !== (config.dueDate || '');
    const netTermsChanged = netTerms !== (config.netTerms ?? -1);

    const storageChanged =
      includeStorage !== !!(config as any).includeStorage ||
      selectedStorageLocationId !== ((config as any).selectedStorageLocationId || null) ||
      storageMinCubicFeet !== ((config as any).storageMinCubicFeet ?? 100);

    const hasChanges = selectedServicesChanged || overridesChanged || adminChargesChanged || additionalChargesChanged || depositChanged || dueDateChanged || netTermsChanged || storageChanged;

    setHasUnsavedChanges(hasChanges);
  }, [selectedServices, serviceOverrides, selectedAdminCharges, additionalCharges, requestedDeposit, dueDate, netTerms, includeStorage, selectedStorageLocationId, storageMinCubicFeet, booking?.invoiceConfig]);

  // Calculate invoice due date based on net terms
  const calculateInvoiceDueDate = () => {
    const today = dayjs();
    if (netTerms === -1) return null; // Due before delivery - no specific date
    if (netTerms === 0) return today; // Due on receipt
    return today.add(netTerms, 'day');
  };

  // Get the final invoice due date
  const finalDueDate = dueDate ? dayjs(dueDate) : calculateInvoiceDueDate();

  // Save invoice configuration to database for billing persistence
  const handleSaveInvoiceConfig = async (notes?: string) => {
    try {
      if (!booking?.id) {
        message.error('No booking found to save configuration');
        return;
      }

      setIsSavingEstimate(true);

      // Build current config
      const currentConfig = {
        selectedServices: Array.from(selectedServices),
        serviceOverrides: Array.from(serviceOverrides.entries())
          .filter(([_, override]) => {
            // Only save overrides that have actual data
            return override.customTotal !== undefined ||
                   override.discountPercentage !== undefined ||
                   override.customVariables !== undefined;
          })
          .map(([id, override]) => ({
            ...override,
            serviceId: id,
          })),
        adminCharges: Array.from(selectedAdminCharges.entries()).map(([_key, data]) => ({
          chargeId: data.chargeId,
          quantity: data.quantity
        })),
        additionalCharges: additionalCharges,
        includeStorage,
        selectedStorageLocationId,
        storageMinCubicFeet,
        storageEstimateData: includeStorage && storageEstimate.monthlyCharge > 0 ? {
          locationName: selectedStorageLocation?.name || null,
          itemCount: storageEstimate.itemCount,
          totalCubicFeet: storageEstimate.totalCubicFeet,
          billableCubicFeet: storageEstimate.billableCubicFeet,
          rate: storageEstimate.rate,
          monthlyCharge: storageEstimate.monthlyCharge,
        } : null,
        // Expo overtime hours: Map serialized as array of [key, otHours] pairs
        expoOvertimeHours: Array.from(expoOvertimeHours.entries()).filter(([_, otHours]) => otHours > 0),
        // Preserve otherCosts from existing config (managed in Profit/Loss tab)
        otherCosts: booking?.invoiceConfig?.otherCosts || [],
        requestedDeposit,
        dueDate: dueDate || undefined,
        netTerms,
        grandTotal: overallTotals.total
      };

      // Create new version entry
      const newVersion: EstimateVersion = {
        id: uuidv4(),
        savedAt: new Date().toISOString(),
        total: overallTotals.total,
        notes: notes,
        config: currentConfig,
      };

      // Add to versions array (no limit now that we have a proper table)
      // Still keep in JSONB for backward compatibility during transition
      const updatedVersions = [newVersion, ...estimateVersions];

      // Build full config data with versions
      const configData = {
        ...currentConfig,
        versions: updatedVersions,
      };

      const loadingKey = 'saving-estimate';
      message.loading({ content: 'Saving estimate...', key: loadingKey, duration: 0 });

      await bookingService.updateBookingInvoiceConfig(booking.id, configData);

      // Update local versions state
      setEstimateVersions(updatedVersions);

      // Invalidate and refetch the booking to get the latest data from server
      await queryClient.invalidateQueries({ queryKey: ['bookings', booking.id] });

      // Refetch the booking data
      const updatedBooking = await bookingService.getBooking(booking.id);
      setBooking(updatedBooking);

      // Reset unsaved changes flag
      setHasUnsavedChanges(false);

      message.success({
        content: `Estimate saved! Total: $${overallTotals.total.toFixed(2)}${notes ? ' - ' + notes : ''}`,
        key: loadingKey,
        duration: 3
      });
    } catch (error: any) {
      console.error('Error saving invoice config:', error);
      message.error({
        content: error.message || 'Failed to save estimate. Please try again.',
        duration: 4
      });
    } finally {
      setIsSavingEstimate(false);
    }
  };

  // Restore a previous version
  const handleRestoreVersion = (version: EstimateVersion) => {
    const config = version.config;

    // Restore all state from the version
    setSelectedServices(new Set(config.selectedServices || []));
    setRequestedDeposit(config.requestedDeposit || 0);
    setDueDate(config.dueDate || '');
    setNetTerms(config.netTerms ?? -1);

    if (config.serviceOverrides && config.serviceOverrides.length > 0) {
      const overrideMap = new Map();
      config.serviceOverrides.forEach((override: any) => {
        if (override.serviceId) {
          overrideMap.set(override.serviceId, override);
        }
      });
      setServiceOverrides(overrideMap);
    } else {
      setServiceOverrides(new Map());
    }

    if (config.adminCharges) {
      const chargesMap = new Map();
      config.adminCharges.forEach((charge: any) => {
        chargesMap.set(charge.chargeId, { chargeId: charge.chargeId, quantity: charge.quantity });
      });
      setSelectedAdminCharges(chargesMap);
    } else {
      setSelectedAdminCharges(new Map());
    }

    // Restore additional charges
    if (config.additionalCharges && Array.isArray(config.additionalCharges)) {
      setAdditionalCharges(config.additionalCharges);
    } else {
      setAdditionalCharges([]);
    }

    // Restore storage config
    setIncludeStorage(!!config.includeStorage);
    setSelectedStorageLocationId(config.selectedStorageLocationId || null);
    setStorageMinCubicFeet(config.storageMinCubicFeet ?? 100);

    // Note: otherCosts are managed in Profit/Loss tab, not restored here

    // Mark as having unsaved changes (since we restored but haven't saved)
    setHasUnsavedChanges(true);

    message.success(`Restored estimate from ${dayjs(version.savedAt).format('MMM D, YYYY h:mm A')}`);
  };

  // Delete a version from history
  const handleDeleteVersion = async (versionId: string) => {
    try {
      // Delete from new table
      await bookingService.deleteEstimateVersion(versionId);
      
      // Update local state
      const updatedVersions = estimateVersions.filter(v => v.id !== versionId);
      setEstimateVersions(updatedVersions);

      // Also update JSONB for backward compatibility
      if (booking?.id) {
        const currentConfig = booking.invoiceConfig || {};
        await bookingService.updateBookingInvoiceConfig(booking.id, {
          ...currentConfig,
          versions: updatedVersions,
        });
      }

      message.success('Version deleted');
    } catch (error: any) {
      console.error('Error deleting version:', error);
      message.error(error.message || 'Failed to delete version');
    }
  };

  // Save invoice to database (separate from config)
  const handleSaveInvoice = async () => {
    try {
      if (selectedServices.size === 0) {
        message.error('Please select at least one service to save');
        return;
      }

      const selectedServicesArray = services.filter(service => selectedServices.has(service.id));
      const invoiceTotal = selectedServicesArray.reduce((total, service) => {
        const cost = calculateServiceCost(service);
        return total + cost.total;
      }, 0) + overallTotals.adminChargesTotal + overallTotals.additionalChargesTotal + overallTotals.storageTotal;

      // Create simplified line items (minimal data)
      const serviceLineItems = selectedServicesArray.map(service => {
        const cost = calculateServiceCost(service);
        const override = serviceOverrides.get(service.id);
        const finalCost = override ? (override.customTotal !== undefined ? override.customTotal : cost.total * (1 - (override.discountPercentage || 0) / 100)) : cost.total;

        return {
          description: `${capitalize(service.serviceType).replace(' Service', '')} - ${service.serial}`,
          quantity: 1,
          rate: finalCost,
          amount: finalCost,
          serviceTicketId: service.id
        };
      });

      // Add admin charges as line items
      const adminChargeLineItems = Array.from(selectedAdminCharges.values()).map(chargeData => {
        const charge = adminCharges.find(c => c.id === chargeData.chargeId);
        const rate = charge?.rate || 0;
        return {
          description: charge?.name || 'Admin Charge',
          quantity: chargeData.quantity,
          rate,
          amount: rate * chargeData.quantity,
          type: 'admin_charge',
        };
      });

      // Add additional charges as line items
      const additionalChargeLineItems = allAdditionalCharges.map(charge => ({
        description: charge.description,
        quantity: charge.quantity,
        rate: charge.rate,
        amount: charge.quantity * charge.rate,
        type: charge.type,
        notes: charge.notes,
        date: charge.date,
        supplyId: charge.supplyId,
      }));

      const lineItems = [...serviceLineItems, ...adminChargeLineItems, ...additionalChargeLineItems];

      // Validate clientId is present
      if (!booking?.clientId) {
        message.error('Booking must have a client assigned to create an invoice');
        return;
      }

      // Simplified invoice data - only essential fields
      const invoiceData: any = {
        bookingId: booking.id,
        clientId: booking.clientId,
        serviceTicketIds: Array.from(selectedServices),
        invoiceDate: dayjs().format('YYYY-MM-DD'),
        subtotal: invoiceTotal,
        tax: 0,
        total: invoiceTotal,
        lineItems,
        notes: `Booking: ${booking?.referenceNumber}${requestedDeposit > 0 ? ` | Deposit: ${formatCurrency(requestedDeposit)}` : ''}${netTerms > 0 ? ` | Net Terms: ${netTerms} days` : netTerms === -1 ? ' | Due Before Delivery' : ''}`
      };

      // Only include dueDate if it's a valid date (not 'Before Delivery')
      if (finalDueDate && finalDueDate.isValid()) {
        invoiceData.dueDate = finalDueDate.format('YYYY-MM-DD');
      }

      const response = await createInvoice(invoiceData);

      if (response.success) {
        // Get the full invoice details to show invoice number
        const invoiceDetails = await getInvoice(response.invoiceId);
        // Close confirmation modal first, then show success modal
        setSaveInvoiceModalVisible(false);
        // Use setTimeout to ensure state updates are batched properly
        setTimeout(() => {
          setSavedInvoice({
            id: invoiceDetails.id,
            invoiceNumber: invoiceDetails.invoiceNumber,
          });
          setSaveInvoiceModalVisible(true);
        }, 0);
        // Refetch invoices to show the new one
        refetchInvoices();
        // Note: Using static message API directly due to messageApi from App.useApp() not being available
        message.success('Invoice saved successfully!');
      } else {
        throw new Error(response.message || 'Failed to save invoice');
      }
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      // Note: Using static message API directly due to messageApi from App.useApp() not being available
      message.error(error.message || 'Failed to save invoice');
      setSaveInvoiceModalVisible(false);
    }
  };

  // Show confirmation modal before saving
  const handleSaveInvoiceClick = () => {
    if (selectedServices.size === 0) {
      message.error('Please select at least one service to save');
      return;
    }
    setSaveInvoiceModalVisible(true);
  };

  // Setup print handler for invoice
  const handlePrintInvoice = useReactToPrint({
    contentRef: invoicePrintRef,
    documentTitle: `AHC_Inv_${booking?.referenceNumber || 'Unknown'}_${dayjs().format('YYYYMMDD')}`,
  });

  // Setup print handler for estimate
  const handlePrintEstimate = useReactToPrint({
    contentRef: estimatePrintRef,
    documentTitle: `AHC_Est_${booking?.referenceNumber || 'Unknown'}_${dayjs().format('YYYYMMDD')}`,
  });

  // Setup print handler for existing invoices
  const handlePrintExistingInvoice = useReactToPrint({
    contentRef: existingInvoicePrintRef,
    documentTitle: (invoiceToPrint?.invoiceNumber || 'Invoice'),
  });

  // Generate estimate PDF
  const handleGenerateEstimate = async () => {
    try {
      if (selectedServices.size === 0) {
        message.error('Please select at least one service to generate estimate');
        return;
      }
      handlePrintEstimate();
    } catch (error: any) {
      console.error('Error generating estimate:', error);
      message.error(error.message || 'Failed to generate estimate');
    }
  };

  // Generate invoice data and trigger print
  const handleGenerateInvoice = async () => {
    try {
      if (selectedServices.size === 0) {
        message.error('Please select at least one service to generate invoice');
        return;
      }

      // Trigger the print dialog
      handlePrintInvoice();

    } catch (error: any) {
      console.error('Error generating invoice:', error);
      message.error(error.message || 'Failed to generate invoice');
    }
  };

  // Get Art Handler role data for staff rates
  const { data: artHandlerRole, isLoading: isLoadingRole } = useGetStaffRoleByName("Art Handler", booking?.clientId);

  // Auto-generate additional charges from billable tasks linked to this booking
  const billableTaskCharges: AdditionalCharge[] = useMemo(() => {
    if (!tasksData?.items) return [];
    return tasksData.items
      .filter((t) => t.billable && t.jobTicketId)
      .map((t) => {
        const hours = t.taskStatus === TaskStatus.COMPLETED && t.actualHours
          ? Number(t.actualHours)
          : t.duration
            ? Number(t.duration)
            : 1;
        const rate = Number(artHandlerRole?.hourlyRate || 100);
        return {
          id: `task-${t.id}`,
          type: 'custom' as const,
          description: `${t.taskRef} - ${t.title}`,
          notes: t.taskStatus === TaskStatus.COMPLETED
            ? `Completed${t.actualHours ? ` (${t.actualHours}h actual)` : ''}`
            : `Estimated${t.duration ? ` (${t.duration}h)` : ''}`,
          date: t.startDate ? dayjs(t.startDate).format('YYYY-MM-DD') : undefined,
          quantity: hours,
          rate,
        };
      });
  }, [tasksData, artHandlerRole]);

  // Combine user-created additional charges with auto-generated task charges
  const allAdditionalCharges = useMemo(() => {
    // Filter out any stale task charges from saved config, then add fresh ones
    const manualCharges = additionalCharges.filter((c) => !c.id.startsWith('task-'));
    return [...manualCharges, ...billableTaskCharges];
  }, [additionalCharges, billableTaskCharges]);

  // Fetch storage locations (rates from backend setup)
  const { data: locationsData } = useGetLocations();
  const storageLocations = useMemo(() => {
    if (!locationsData?.items) return [];
    // Flatten parent + children into a single list with rates
    const flat: { id: string; name: string; rate: number; minimumCubicFeet: number }[] = [];
    for (const loc of locationsData.items) {
      if (loc.ratePerCubicFt) {
        flat.push({ id: loc.id, name: loc.name, rate: Number(loc.ratePerCubicFt) || 0, minimumCubicFeet: Number(loc.minimumCubicFeet) || 0 });
      }
      if (loc.children) {
        for (const child of loc.children) {
          if (child.ratePerCubicFt) {
            flat.push({ id: child.id, name: `${loc.name} → ${child.name}`, rate: Number(child.ratePerCubicFt) || 0, minimumCubicFeet: Number(child.minimumCubicFeet) || 0 });
          }
        }
      }
    }
    return flat;
  }, [locationsData]);

  // Resolve the selected location's rate
  const selectedStorageLocation = useMemo(() => {
    return storageLocations.find(l => l.id === selectedStorageLocationId) || null;
  }, [storageLocations, selectedStorageLocationId]);

  // Compute storage estimate from all unique inventory across service tickets
  const storageEstimate = useMemo(() => {
    if (!booking?.serviceTicket) return { totalCubicFeet: 0, billableCubicFeet: 0, rate: 0, monthlyCharge: 0, itemCount: 0 };

    // Deduplicate inventory across service tickets
    const seen = new Set<string>();
    let totalCubicFeet = 0;
    let itemCount = 0;

    for (const service of booking.serviceTicket) {
      if (!service.inventories) continue;
      for (const inv of service.inventories) {
        if (seen.has(inv.id)) continue;
        seen.add(inv.id);
        const vol = ((inv.length || 0) * (inv.width || 0) * (inv.height || 0)) / 1728;
        totalCubicFeet += vol;
        itemCount++;
      }
    }

    // Apply 10% buffer (matches backend storage calculation)
    totalCubicFeet *= 1.1;

    const billableCubicFeet = Math.max(totalCubicFeet, storageMinCubicFeet);
    const rate = selectedStorageLocation?.rate || 0;
    const monthlyCharge = billableCubicFeet * rate;

    return { totalCubicFeet, billableCubicFeet, rate, monthlyCharge, itemCount };
  }, [booking?.serviceTicket, selectedStorageLocation?.rate, storageMinCubicFeet]);

  // Get add-on services data for pricing
  const { data: addOnServices, isLoading: isLoadingAddOns } = useQuery({
    queryKey: ["add-on-services"],
    queryFn: () => addOnServicesApi.getAll(),
  });

  const isLoading = isLoadingRole || isLoadingAddOns;

  // Calculate service costs
  const calculateServiceCost = useMemo(() => {
    return (service: any) => {
      const override = serviceOverrides.get(service.id);

      const travelHours = getTravelTimeHours(service);
      const durationHours = getDurationHours(service);
      const totalHours = override?.customVariables?.hours || (travelHours + durationHours);

      let serviceCost = 0;
      let staffCost = 0;
      let vesselCost = 0;
      let staffCount = override?.customVariables?.staffCount || service.staffs?.length || 1; // Default varies by service type
      const staffHourlyRate = override?.customVariables?.staffRate || artHandlerRole?.hourlyRate || 100; // Default $100/hr
      let vesselHourlyRate = override?.customVariables?.vesselRate || 0;

      // Different calculation logic based on service type
      if (
        service.serviceType === ServiceType.COLLECTION ||
        service.serviceType === ServiceType.DELIVERY
      ) {
        // Collection/Delivery: (Staff + Vehicle) × Total Hours (travel + site time)
        // Use override staff count if provided, otherwise use actual staff or default to 2
        if (!override?.customVariables?.staffCount) {
          staffCount = service.staffs?.length || 2; // Default to 2 staff for transport
        }
        staffCost = staffCount * staffHourlyRate * totalHours;

        // Vehicle cost (only if vessel is assigned)
        if (service.vessel) {
          vesselHourlyRate = override?.customVariables?.vesselRate || service.vessel.hourlyRate || 90;
          vesselCost = vesselHourlyRate * totalHours;
        }

        serviceCost = staffCost + vesselCost;

      } else if (service.serviceType === ServiceType.ON_SITE_SERVICE) {
        // Check if this is an EXPO service with positions
        if (service.isExpoDay && service.positions && service.positions.length > 0) {
          // EXPO Mode: Use positions data (straight time, no $300 minimum)
          // Calculate labor cost from positions with split regular/OT hours
          // OT hours are billed at 1.5x rate (e.g., 11hr job with 3 OT = 8hr regular + 3hr at 1.5x)
          staffCost = service.positions.reduce((total: number, pos: any, posIndex: number) => {
            const posQty = Number(pos.quantity) || 1;
            const posDuration = Number(pos.duration) || 0;
            const baseRate = Number(pos.hourlyRate) || Number(pos.position?.hourlyRate) || 0;

            // Get OT hours for this position (0 if not set)
            const otKey = `${service.id}_${posIndex}`;
            const otHours = expoOvertimeHours.get(otKey) || 0;

            // Calculate regular hours (total duration minus OT hours, minimum 0)
            const regularHours = Math.max(0, posDuration - otHours);

            // Calculate cost: (regular hours × rate) + (OT hours × rate × 1.5)
            const regularCost = posQty * regularHours * baseRate;
            const otCost = posQty * otHours * baseRate * 1.5;

            return total + regularCost + otCost;
          }, 0);

          // Total staff count from positions (for display purposes)
          staffCount = service.positions.reduce((total: number, pos: any) => total + (Number(pos.quantity) || 1), 0);
          serviceCost = staffCost; // No minimum for expo mode
        } else {
          // Regular On-Site Service: Staff Only × Site Time with $300 minimum per staff
          // Use override staff count if provided, otherwise use actual staff or default to 1
          if (!override?.customVariables?.staffCount) {
            staffCount = service.staffs?.length || 1; // Default to 1 staff for on-site
          }
          const siteTimeOnly = override?.customVariables?.hours || durationHours; // No travel time for on-site
          staffCost = staffCount * staffHourlyRate * siteTimeOnly;

          // $300 minimum per staff member
          const minimumCharge = 300 * staffCount;
          serviceCost = Math.max(staffCost, minimumCharge);
        }

      } else if (service.serviceType === ServiceType.WAREHOUSE_SERVICE) {
        // Warehouse: Volume-based pricing ($1/ft³ with $75 minimum) - no hourly rates
        const totalCubicFeet = override?.customVariables?.cubicFeet || service.inventories?.reduce((total: number, inventory: any) => {
          const volume = ((inventory.length || 1) * (inventory.width || 1) * (inventory.height || 1)) / 1728;
          return total + volume;
        }, 0) || 60; // Default 60 cubic feet

        const warehouseRate = 1; // $1 per cubic foot
        const minimumCharge = 75; // $75 minimum
        serviceCost = Math.max(totalCubicFeet * warehouseRate, minimumCharge);

      } else if (service.serviceType === ServiceType.FABRICATION_SERVICE) {
        // Fabrication: Custom rates - typically per square foot or hourly for specialized work
        // Use override staff count if provided, otherwise use actual staff or default to 1
        if (!override?.customVariables?.staffCount) {
          staffCount = service.staffs?.length || 1; // Default to 1 specialist
        }
        // For now, use hourly rate for fabrication (could be enhanced with sq ft rates)
        const fabricationHours = override?.customVariables?.hours || durationHours; // Just site time, no travel
        staffCost = staffCount * (staffHourlyRate * 1.5) * fabricationHours; // 1.5x rate for specialized work
        serviceCost = staffCost;

      } else {
        // Fallback for other service types
        staffCost = staffCount * staffHourlyRate * totalHours;
        serviceCost = staffCost;
      }

      // Recalculate serviceCost with adjusted costs (only for service types that use staff/vessel costs)
      if (
        service.serviceType === ServiceType.COLLECTION ||
        service.serviceType === ServiceType.DELIVERY
      ) {
        serviceCost = staffCost + vesselCost;
      } else if (service.serviceType === ServiceType.ON_SITE_SERVICE) {
        // For expo services, no minimum charge
        if (service.isExpoDay && service.positions && service.positions.length > 0) {
          serviceCost = staffCost; // No minimum for expo mode
        } else {
          // Reapply minimum charge after adjustment
          const minimumCharge = 300 * staffCount;
          serviceCost = Math.max(staffCost, minimumCharge);
        }
      } else if (service.serviceType === ServiceType.FABRICATION_SERVICE) {
        serviceCost = staffCost;
      } else if (service.serviceType !== ServiceType.WAREHOUSE_SERVICE) {
        // Fallback for other service types (but not warehouse)
        serviceCost = staffCost;
      }
      // Note: WAREHOUSE_SERVICE serviceCost is already set correctly above and doesn't use staff/vessel

      // Calculate supplies cost
      const suppliesCost = service.supplyItems?.reduce((total: number, supply: any) => {
        return total + ((supply.quantity || 1) * (supply.unitCost || 0));
      }, 0) || 0;

      // Calculate add-on services cost based on service type
      let addOnCost = 0;

      if (service.addOnServices && service.addOnServices.length > 0) {
        // For Collection, Delivery, and Site Service: add-ons are included (no extra charge)
        if (
          service.serviceType === ServiceType.COLLECTION ||
          service.serviceType === ServiceType.DELIVERY ||
          service.serviceType === ServiceType.ON_SITE_SERVICE
        ) {
          addOnCost = 0; // General services add-ons are included
        }
        // For Warehouse and Fabrication: calculate using volume/inventory
        else if (
          service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
          service.serviceType === ServiceType.FABRICATION_SERVICE
        ) {
          // Calculate total volume from inventory for volume-based pricing
          const totalCubicFeet = service.inventories?.reduce((total: number, inventory: any) => {
            const volume = ((inventory.length || 1) * (inventory.width || 1) * (inventory.height || 1)) / 1728;
            return total + volume;
          }, 0) || 60; // Default 60 cubic feet

          addOnCost = service.addOnServices.reduce((total: number, addon: any) => {
            const addOnService = addOnServices?.find((aos: AddOnServiceDto) => aos.id === addon.id);
            if (addOnService?.rate) {
              if (addOnService.unit === 'CU_FT') {
                // Volume-based pricing for warehouse/fabrication with minimum pricing
                const volumeRate = addOnService.rate * totalCubicFeet;
                const minimumRate = addOnService.minimumCharge || 0;
                return total + Math.max(volumeRate, minimumRate);
              } else if (addOnService.unit === 'HOURLY') {
                return total + (addOnService.rate * totalHours);
              } else if (addOnService.unit === 'EACH') {
                return total + addOnService.rate;
              }
            }
            return total;
          }, 0);
        }
      }

      // Calculate base total
      const baseTotal = serviceCost + suppliesCost + addOnCost;

      // Apply discount if override exists
      let finalTotal = baseTotal;
      if (override?.discountPercentage) {
        const discountAmount = baseTotal * (override.discountPercentage / 100);
        finalTotal = baseTotal - discountAmount;
      }

      // Use custom total if provided (and it's greater than 0)
      if (override?.customTotal !== undefined && override.customTotal > 0) {
        finalTotal = override.customTotal;
      }

      return {
        serviceCost,
        suppliesCost,
        addOnCost,
        baseTotal,
        discountPercentage: override?.discountPercentage || 0,
        discountAmount: override?.discountPercentage ? baseTotal * (override.discountPercentage / 100) : 0,
        total: finalTotal,
        isOverridden: !!(override && (override.customTotal !== undefined || override.discountPercentage !== undefined || override.customVariables !== undefined)),
        breakdown: {
          staffCount,
          staffHourlyRate,
          staffCost,
          vesselHourlyRate,
          vesselCost,
          totalHours,
          travelHours,
          durationHours,
        }
      };
    };
  }, [artHandlerRole, addOnServices, serviceOverrides, booking, expoOvertimeHours]);

  // Compute services before early return
  const services = useMemo(() => {
    if (!booking?.serviceTicket) return [];
    return booking.serviceTicket.sort((a, b) => {
      return new Date(a.serviceDatetime).getTime() - new Date(b.serviceDatetime).getTime();
    });
  }, [booking]);

  // Group services based on groupBy state
  const groupedServices = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', title: null, services }];
    }

    if (groupBy === 'type') {
      const groups = new Map<string, any[]>();
      services.forEach((service) => {
        const type = service.serviceType;
        if (!groups.has(type)) {
          groups.set(type, []);
        }
        groups.get(type)!.push(service);
      });

      return Array.from(groups.entries()).map(([type, services]) => ({
        key: type,
        title: capitalize(type).replace(' Service', ''),
        services: services.sort((a, b) =>
          new Date(a.serviceDatetime).getTime() - new Date(b.serviceDatetime).getTime()
        ),
      }));
    }

    if (groupBy === 'date') {
      const groups = new Map<string, any[]>();
      services.forEach((service) => {
        const dateKey = dayjs(service.serviceDatetime).format('YYYY-MM-DD');
        if (!groups.has(dateKey)) {
          groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(service);
      });

      return Array.from(groups.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([dateKey, services]) => ({
          key: dateKey,
          title: dayjs(dateKey).format('dddd, MMMM D, YYYY'),
          services,
        }));
    }

    return [{ key: 'all', title: null, services }];
  }, [services, groupBy]);

  // Prepare invoice data for printing
  const invoiceData = useMemo(() => {
    if (selectedServices.size === 0) return null;

    const selectedServicesArray = services.filter(service => selectedServices.has(service.id));
    const invoiceTotal = selectedServicesArray.reduce((total, service) => {
      const cost = calculateServiceCost(service);
      const override = serviceOverrides.get(service.id);
      return total + (override ? (override.customTotal !== undefined ? override.customTotal : cost.total * (1 - (override.discountPercentage || 0) / 100)) : cost.total);
    }, 0);

    // Calculate admin charges total
    const adminChargesTotal = Array.from(selectedAdminCharges.values()).reduce((total, chargeData) => {
      const charge = adminCharges.find(c => c.id === chargeData.chargeId);
      if (charge && charge.rate) {
        return total + (charge.rate * chargeData.quantity);
      }
      return total;
    }, 0);

    // Calculate additional charges total
    const additionalChargesTotal = allAdditionalCharges.reduce((total, charge) => {
      return total + (charge.rate * charge.quantity);
    }, 0);

    const subtotal = invoiceTotal + adminChargesTotal + additionalChargesTotal;

    return {
      invoiceNumber: `INV${booking?.referenceNumber}`,
      invoiceDate: dayjs().format('YYYY-MM-DD'),
      dueDate: finalDueDate?.format('YYYY-MM-DD') || 'Before Delivery',
      booking: {
        referenceNumber: booking?.referenceNumber,
        project: booking?.project
      },
      client: {
        companyName: booking?.client?.companyName || booking?.tempClientData?.name,
        firstName: booking?.client?.firstName,
        lastName: booking?.client?.lastName,
        email: booking?.client?.email || booking?.tempClientData?.email,
        contactPhone: booking?.client?.contactPhone || booking?.tempClientData?.phone,
        address: booking?.client?.shippingAddresses?.[0],
        isTempClient: !booking?.client && !!booking?.tempClientData
      },
      services: selectedServicesArray.map(service => {
        const cost = calculateServiceCost(service);
        const override = serviceOverrides.get(service.id);
        const finalCost = override ? (override.customTotal !== undefined ? override.customTotal : cost.total * (1 - (override.discountPercentage || 0) / 100)) : cost.total;

        // Build expo positions data if applicable
        let expoPositions = null;
        if (service.isExpoDay && service.positions && service.positions.length > 0) {
          expoPositions = service.positions.map((pos: any, posIndex: number) => {
            const posQty = Number(pos.quantity) || 1;
            const posDuration = Number(pos.duration) || 0;
            const baseRate = Number(pos.hourlyRate) || Number(pos.position?.hourlyRate) || 0;
            const otKey = `${service.id}_${posIndex}`;
            const otHours = expoOvertimeHours.get(otKey) || 0;

            // Calculate regular and OT hours/costs
            const regularHours = Math.max(0, posDuration - otHours);
            const regularCost = posQty * regularHours * baseRate;
            const otCost = posQty * otHours * baseRate * 1.5;
            const posSubtotal = regularCost + otCost;

            // Calculate time out from start time + duration
            let timeIn = pos.startTime || '08:00';
            let timeOut = '';
            if (timeIn && posDuration > 0) {
              const [hours, minutes] = timeIn.split(':').map(Number);
              const totalMinutes = hours * 60 + minutes + (posDuration * 60);
              const outHours = Math.floor(totalMinutes / 60) % 24;
              const outMinutes = Math.floor(totalMinutes % 60);
              timeOut = `${outHours.toString().padStart(2, '0')}:${outMinutes.toString().padStart(2, '0')}`;
            }

            return {
              positionName: pos.position?.name || 'Unknown Position',
              quantity: posQty,
              duration: posDuration,
              regularHours,
              otHours,
              hourlyRate: baseRate,
              timeIn,
              timeOut,
              subtotal: posSubtotal,
              totalHours: posQty * posDuration, // Total staff-hours for this position
            };
          });
        }

        return {
          serial: service.serial,
          serviceType: capitalize(service.serviceType).replace(' Service', ''),
          serviceDatetime: dayjs(service.serviceDatetime).format('YYYY-MM-DD'),
          client: {
            companyName: service.tempLocationData?.name || service.client?.companyName,
            firstName: service.client?.firstName,
            lastName: service.client?.lastName
          },
          location: {
            city: service.location?.city || service.tempLocationData?.city,
            state: service.location?.state || service.tempLocationData?.state
          },
          travelTime: service.travelTime,
          duration: service.duration,
          scope: service.scope,
          addOnServices: service.addOnServices?.map(addon => addon.name) || [],
          supplies: service.supplyItems?.map(supply => supply.supply?.name).filter((name): name is string => name !== undefined) || [],
          packageCount: service.inventories?.length || service.estimatedItemCount || service.estimateTemplates?.reduce((sum, t) => sum + (t.quantity || 0), 0) || 0,
          originalCost: cost.total,
          finalCost,
          isOverridden: !!(override && (override.customTotal !== undefined || override.discountPercentage !== undefined || override.customVariables !== undefined)),
          // Expo data
          isExpoDay: service.isExpoDay || false,
          dayLabel: service.dayLabel || null,
          expoPositions,
        };
      }),
      adminCharges: Array.from(selectedAdminCharges.entries()).map(([_key, chargeData]) => {
        const charge = adminCharges.find(c => c.id === chargeData.chargeId);
        return {
          name: charge?.name || 'Admin Charge',
          rate: charge?.rate || 0,
          unit: charge?.unit || 'unit',
          quantity: chargeData.quantity,
          total: (charge?.rate || 0) * chargeData.quantity
        };
      }),
      additionalCharges: allAdditionalCharges.map(charge => ({
        description: charge.description,
        notes: charge.notes,
        date: charge.date,
        quantity: charge.quantity,
        rate: charge.rate,
        total: charge.quantity * charge.rate,
        type: charge.type
      })),
      storage: includeStorage && storageEstimate.monthlyCharge > 0 ? {
        locationName: selectedStorageLocation?.name || 'Storage',
        itemCount: storageEstimate.itemCount,
        totalCubicFeet: storageEstimate.totalCubicFeet,
        billableCubicFeet: storageEstimate.billableCubicFeet,
        minimumCubicFeet: storageMinCubicFeet,
        rate: storageEstimate.rate,
        total: storageEstimate.monthlyCharge,
      } : null,
      financial: {
        subtotal,
        depositAmount: requestedDeposit,
        balanceDue: subtotal - requestedDeposit,
        netTerms,
        paymentTerms: netTerms === -1 ? 'Due Before Delivery' : netTerms === 0 ? 'Due on Receipt' : `Net ${netTerms} Days`
      }
    };
  }, [selectedServices, services, serviceOverrides, selectedAdminCharges, adminCharges, additionalCharges, includeStorage, storageEstimate, selectedStorageLocation, storageMinCubicFeet, requestedDeposit, netTerms, finalDueDate, booking, calculateServiceCost]);

  // Calculate overall totals
  const overallTotals = useMemo(() => {
    const servicesSubtotal = services.reduce((total, service) => {
      const cost = calculateServiceCost(service);
      return total + cost.total;
    }, 0);

    // Calculate admin charges total
    const adminChargesTotal = Array.from(selectedAdminCharges.values()).reduce((total, chargeData) => {
      const charge = adminCharges.find(c => c.id === chargeData.chargeId);
      if (charge && charge.rate) {
        return total + (charge.rate * chargeData.quantity);
      }
      return total;
    }, 0);

    // Calculate additional charges total
    const additionalChargesTotal = allAdditionalCharges.reduce((total, charge) => {
      return total + (charge.rate * charge.quantity);
    }, 0);

    // Storage estimate (only when user has toggled it on)
    const storageTotal = includeStorage ? storageEstimate.monthlyCharge : 0;

    const subtotal = servicesSubtotal + adminChargesTotal + additionalChargesTotal + storageTotal;

    // Apply rate type discount if client has a rate type
    let discountAmount = 0;
    let discountPercentage = 0;
    const bookingClient = booking?.client;
    if (bookingClient?.rateType && rateTypeDiscounts) {
      const discount = rateTypeDiscounts.find(d => d.rateType === bookingClient.rateType);
      if (discount && discount.discountPercentage !== 0) {
        discountPercentage = discount.discountPercentage;
        // Calculate discount amount: positive = discount (subtract), negative = premium (add)
        discountAmount = subtotal * (discountPercentage / 100);
      }
    }

    // Subtract discount amount: if negative (premium), subtracting negative = adding
    const total = subtotal - discountAmount;

    return {
      subtotal,
      total,
      adminChargesTotal,
      additionalChargesTotal,
      storageTotal,
      discountAmount,
      discountPercentage,
    };
  }, [services, calculateServiceCost, selectedAdminCharges, adminCharges, allAdditionalCharges, includeStorage, storageEstimate.monthlyCharge, booking?.client?.rateType, rateTypeDiscounts]);

  // Toggle expanded state for services
  const toggleService = (serviceId: string) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(serviceId)) {
      newExpanded.delete(serviceId);
    } else {
      newExpanded.add(serviceId);
    }
    setExpandedServices(newExpanded);
  };

  // Toggle service selection for invoice
  const toggleServiceSelection = (serviceId: string) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  // Select all services
  const selectAllServices = () => {
    setSelectedServices(new Set(services.map(s => s.id)));
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedServices(new Set());
  };

  // Expand all services
  const expandAllServices = () => {
    setExpandedServices(new Set(services.map(s => s.id)));
  };

  // Collapse all services
  const collapseAllServices = () => {
    setExpandedServices(new Set());
  };

  // Open override modal for a service
  const openOverrideModal = (service: any) => {
    const currentOverride = serviceOverrides.get(service.id);
    setCurrentServiceOverride({
      service,
      override: currentOverride || {
        serviceId: service.id,
        customVariables: {},
        discountPercentage: 0,
      }
    });
    setOverrideModalOpen(true);
  };

  // Close override modal
  const closeOverrideModal = () => {
    setOverrideModalOpen(false);
    setCurrentServiceOverride(null);
  };

  // Save override
  const saveOverride = (override: ServiceOverride) => {
    const newOverrides = new Map(serviceOverrides);
    newOverrides.set(override.serviceId, override);
    setServiceOverrides(newOverrides);
    closeOverrideModal();
  };

  // Remove override
  const removeOverride = (serviceId: string) => {
    const newOverrides = new Map(serviceOverrides);
    newOverrides.delete(serviceId);
    setServiceOverrides(newOverrides);
    closeOverrideModal();
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Early return if booking is not loaded yet (after all hooks)
  if (!booking) {
    return <Spin size="large" className="flex justify-center items-center min-h-[400px]" />;
  }

  return (
    <div 
      className="transition-all duration-300 ease-in-out"
      style={{ marginRight: sidebarOpen ? '360px' : '0px' }}
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Billing</h3>
      </div>

      <Tabs
        defaultActiveKey="estimate"
        items={[
          {
            key: "estimate",
            label: "Estimate",
            children: (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium text-lg">Service Estimate</h4>
                      <p className="text-gray-600 text-sm">Detailed breakdown of services, staff, and supplies</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {!isLoading && services.length > 0 && (
                        <>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600 font-medium">Group by:</span>
                            <Select
                              value={groupBy}
                              onChange={(value) => setGroupBy(value)}
                              size="small"
                              style={{ width: 120 }}
                              options={[
                                { value: 'date', label: 'Date' },
                                { value: 'type', label: 'Service Type' },
                                { value: 'none', label: 'None' },
                              ]}
                            />
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={selectAllServices}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Select All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={clearAllSelections}
                              className="text-xs text-gray-600 hover:text-gray-800"
                            >
                              Clear All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={expandAllServices}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Expand All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={collapseAllServices}
                              className="text-xs text-gray-600 hover:text-gray-800"
                            >
                              Collapse All
                            </button>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs text-gray-500">
                              {selectedServices.size} of {services.length} selected
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 ml-3">
                            {hasUnsavedChanges && (
                              <span className="text-xs text-orange-600 font-medium">
                                • Unsaved changes
                              </span>
                            )}
                            <Button
                              type={hasUnsavedChanges ? "primary" : "default"}
                              onClick={() => handleSaveInvoiceConfig()}
                              className={hasUnsavedChanges ? "" : "border-green-600 text-green-600"}
                            >
                              {hasUnsavedChanges ? "Save Configuration" : "✓ Saved"}
                            </Button>
                          </div>
                        </>
                      )}
                      {isLoading && (
                        <div className="flex items-center text-blue-600">
                          <Spin size="small" className="mr-2" />
                          <span className="text-sm">Loading rates...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {services.length > 0 ? (
                  <div className="divide-y">
                    {groupedServices.map((group) => (
                      <Fragment key={group.key}>
                        {group.title && (
                          <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
                            <h5 className="font-semibold text-blue-900 text-sm uppercase tracking-wide">
                              {group.title}
                            </h5>
                            <p className="text-xs text-blue-700 mt-1">
                              {group.services.length} service{group.services.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        )}
                        {group.services.map((service) => {
                          const travelHours = getTravelTimeHours(service);
                          const durationHours = getDurationHours(service);
                          const totalHours = travelHours + durationHours;
                          const costBreakdown = calculateServiceCost(service);
                          const isExpanded = expandedServices.has(service.id);

                          return (
                            <div key={service.id} className="border-b last:border-b-0">
                          {/* Collapsible Service Header */}
                          <div
                            className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                            onClick={() => toggleService(service.id)}
                          >
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={selectedServices.has(service.id)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleServiceSelection(service.id);
                                  }}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                {isExpanded ? (
                                  <ChevronDown size={20} className="text-gray-400" />
                                ) : (
                                  <ChevronRight size={20} className="text-gray-400" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center space-x-3">
                                  <h5 className="font-semibold text-base">
                                    {capitalize(service.serviceType).replace(' Service', '')}
                                  </h5>
                                  <span className="text-gray-500">#{service.serial}</span>
                                  {/* Status Badge */}
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    service.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                    service.status === 'IN-PROGRESS' ? 'bg-blue-100 text-blue-800' :
                                    service.status === 'SCHEDULED' ? 'bg-gray-100 text-gray-800' :
                                    service.status === 'ESTIMATED' ? 'bg-amber-100 text-amber-800' :
                                    service.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {capitalize(service.status || 'pending').replace('-', ' ')}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                  <span className="font-medium">
                                    {service.tempLocationData?.name || service.client?.companyName || booking?.tempClientData?.name || "Quick Client"}
                                  </span>
                                  <span>•</span>
                                  <span>
                                    {(() => {
                                      // Combine date from serviceDatetime with time from startDatetime
                                      let displayDateTime;
                                      if (service.serviceDatetime && service.startDatetime) {
                                        const serviceDate = dayjs(service.serviceDatetime);
                                        const startTimeData = dayjs(service.startDatetime);
                                        displayDateTime = serviceDate
                                          .hour(startTimeData.hour())
                                          .minute(startTimeData.minute())
                                          .second(startTimeData.second());
                                      } else {
                                        displayDateTime = dayjs(service.serviceDatetime || service.startDatetime);
                                      }
                                      return displayDateTime.format("dddd, MMM D, YYYY [at] h:mm A");
                                    })()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold">
                                {isLoading ? (
                                  <div className="flex items-center justify-end">
                                    <Spin size="small" className="mr-2" />
                                    <span className="text-gray-400 text-sm">Calculating...</span>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openOverrideModal(service);
                                    }}
                                    className={`text-xl font-bold transition-colors px-3 py-1 rounded ${
                                      costBreakdown.isOverridden
                                        ? 'bg-orange-50 text-orange-700 border border-orange-200'
                                        : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                                    }`}
                                    title="Click to override pricing"
                                  >
                                    {formatCurrency(costBreakdown.total)}
                                    {costBreakdown.isOverridden && (
                                      <span className="ml-1 text-sm">*</span>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Service Details */}
                          {isExpanded && (
                            <div className="px-4 pb-4">
                              <div className="bg-gray-50 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="text-left px-4 py-3 font-medium">Description</th>
                                  <th className="text-center px-4 py-3 font-medium">Travel Time</th>
                                  <th className="text-center px-4 py-3 font-medium">Site Time</th>
                                  <th className="text-center px-4 py-3 font-medium">Staff</th>
                                  <th className="text-center px-4 py-3 font-medium">Vehicle</th>
                                  <th className="text-right px-4 py-3 font-medium">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                <tr>
                                  <td className="px-4 py-3">
                                    <div className="font-medium">
                                      {service.serviceType === ServiceType.COLLECTION && "Collection"}
                                      {service.serviceType === ServiceType.DELIVERY && "Delivery"}
                                      {service.serviceType === ServiceType.ON_SITE_SERVICE && "On-Site"}
                                      {service.serviceType === ServiceType.WAREHOUSE_SERVICE && "Warehouse"}
                                      {service.serviceType === ServiceType.FABRICATION_SERVICE && "Fabrication"}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      Total: {totalHours.toFixed(2)} hours
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {service.vessel ? `${travelHours.toFixed(2)}h` : "N/A"}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {durationHours.toFixed(2)}h
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <div>
                                      {service.staffs && service.staffs.length > 0 ? (
                                        <div className="font-medium">{service.staffs.length} staff</div>
                                      ) : (
                                        <span className="text-gray-400">
                                          {!isLoading ? `${costBreakdown.breakdown.staffCount} staff (default)` : "TBD"}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <div>
                                      {service.vessel ? (
                                        <>
                                          <div className="font-medium">{service.vessel.name}</div>
                                          <div className="text-xs text-gray-500">
                                            {service.vessel.vesselType?.name || "Vehicle"}
                                          </div>
                                        </>
                                      ) : (
                                        <span className="text-gray-400">
                                          {!isLoading ? "Default Vehicle" : "TBD"}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right font-medium">
                                    {isLoading ? (
                                      <Spin size="small" />
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openOverrideModal(service);
                                        }}
                                        className={`hover:bg-blue-50 hover:text-blue-700 px-2 py-1 rounded transition-colors ${
                                          costBreakdown.isOverridden
                                            ? 'bg-orange-50 text-orange-700 border border-orange-200'
                                            : 'hover:border border-transparent'
                                        }`}
                                        title="Click to override pricing"
                                      >
                                        {formatCurrency(costBreakdown.total)}
                                        {costBreakdown.isOverridden && (
                                          <span className="ml-1 text-xs">*</span>
                                        )}
                                      </button>
                                    )}
                                  </td>
                                </tr>

                                {/* Expo Positions Section */}
                                {service.isExpoDay && service.positions && service.positions.length > 0 && (
                                  <>
                                    <tr className="bg-purple-50">
                                      <td colSpan={7} className="px-4 py-2">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center">
                                            <div className="w-4 h-4 mr-2">
                                              <svg viewBox="0 0 16 16" fill="currentColor" className="text-purple-600">
                                                <path d="M8 2a1 1 0 0 1 1 1v4h4a1 1 0 1 1 0 2H9v4a1 1 0 1 1-2 0V9H3a1 1 0 1 1 0-2h4V3a1 1 0 0 1 1-1z"/>
                                              </svg>
                                            </div>
                                            <span className="font-medium text-purple-800">Expo Positions ({service.positions.length})</span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newMap = new Map(expoOvertimeHours);
                                              service.positions.forEach((pos: any, posIndex: number) => {
                                                const posDuration = Number(pos.duration) || 0;
                                                if (posDuration > 8) {
                                                  const otKey = `${service.id}_${posIndex}`;
                                                  newMap.set(otKey, posDuration - 8);
                                                }
                                              });
                                              setExpoOvertimeHours(newMap);
                                              setHasUnsavedChanges(true);
                                            }}
                                            className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded transition-colors"
                                            title="Auto-calculate OT for positions over 8 hours"
                                          >
                                            Auto OT (8+ hrs)
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                    {/* Expo positions table header */}
                                    <tr className="bg-purple-25">
                                      <td className="px-8 py-1 text-xs font-medium text-purple-700">Position (Rate/hr)</td>
                                      <td className="px-4 py-1 text-center text-xs font-medium text-purple-700">Qty</td>
                                      <td className="px-4 py-1 text-center text-xs font-medium text-purple-700">Time In</td>
                                      <td className="px-4 py-1 text-center text-xs font-medium text-purple-700">Time Out</td>
                                      <td className="px-4 py-1 text-center text-xs font-medium text-purple-700">Duration</td>
                                      <td className="px-4 py-1 text-center text-xs font-medium text-purple-700">OT Hrs</td>
                                      <td className="px-4 py-1 text-right text-xs font-medium text-purple-700">Amount</td>
                                    </tr>
                                    {service.positions.map((pos: any, posIndex: number) => {
                                      const posQty = Number(pos.quantity) || 1;
                                      const posDuration = Number(pos.duration) || 0;
                                      const baseRate = Number(pos.hourlyRate) || Number(pos.position?.hourlyRate) || 0;
                                      const otKey = `${service.id}_${posIndex}`;
                                      const otHours = expoOvertimeHours.get(otKey) || 0;

                                      // Calculate regular and OT hours
                                      const regularHours = Math.max(0, posDuration - otHours);
                                      const regularCost = posQty * regularHours * baseRate;
                                      const otCost = posQty * otHours * baseRate * 1.5;
                                      const posSubtotal = regularCost + otCost;

                                      // Calculate time out from start time + duration
                                      let timeIn = pos.startTime || '08:00';
                                      let timeOut = '';
                                      if (timeIn && posDuration > 0) {
                                        const [hours, minutes] = timeIn.split(':').map(Number);
                                        const totalMinutes = hours * 60 + minutes + (posDuration * 60);
                                        const outHours = Math.floor(totalMinutes / 60) % 24;
                                        const outMinutes = Math.floor(totalMinutes % 60);
                                        timeOut = `${outHours.toString().padStart(2, '0')}:${outMinutes.toString().padStart(2, '0')}`;
                                      }

                                      // Format time for display (convert 24h to 12h)
                                      const formatTime = (time: string) => {
                                        if (!time) return '-';
                                        const [h, m] = time.split(':').map(Number);
                                        const period = h >= 12 ? 'PM' : 'AM';
                                        const hour12 = h % 12 || 12;
                                        return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
                                      };

                                      return (
                                        <tr key={posIndex} className="bg-purple-25 border-t border-purple-100">
                                          <td className="px-8 py-2 text-sm">
                                            <div className="flex items-center">
                                              <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                                              <span className="font-medium">{pos.position?.name || 'Unknown Position'}</span>
                                              <span className="ml-1 text-gray-500">({formatCurrency(baseRate)}/hr)</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 text-center text-sm">{posQty}</td>
                                          <td className="px-4 py-2 text-center text-sm">{formatTime(timeIn)}</td>
                                          <td className="px-4 py-2 text-center text-sm">{formatTime(timeOut)}</td>
                                          <td className="px-4 py-2 text-center text-sm">{posDuration}h</td>
                                          <td className="px-4 py-2 text-center text-sm">
                                            <input
                                              type="number"
                                              min="0"
                                              max={posDuration}
                                              step="0.5"
                                              value={otHours || ''}
                                              placeholder="0"
                                              onChange={(e) => {
                                                const newMap = new Map(expoOvertimeHours);
                                                const value = parseFloat(e.target.value) || 0;
                                                // Cap OT hours at total duration
                                                const cappedValue = Math.min(value, posDuration);
                                                if (cappedValue > 0) {
                                                  newMap.set(otKey, cappedValue);
                                                } else {
                                                  newMap.delete(otKey);
                                                }
                                                setExpoOvertimeHours(newMap);
                                                setHasUnsavedChanges(true);
                                              }}
                                              className="w-14 px-1 py-0.5 text-center text-sm border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                                              title="Enter overtime hours (billed at 1.5x rate)"
                                            />
                                          </td>
                                          <td className="px-4 py-2 text-right text-sm font-medium text-purple-700">
                                            <div>
                                              {formatCurrency(posSubtotal)}
                                              {otHours > 0 && (
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                  <div>{regularHours}h × {formatCurrency(baseRate)} = {formatCurrency(regularCost)}</div>
                                                  <div className="text-orange-600">{otHours}h × {formatCurrency(baseRate * 1.5)} (1.5x) = {formatCurrency(otCost)}</div>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {/* Expo positions total */}
                                    <tr className="bg-purple-100 border-t border-purple-200">
                                      <td colSpan={4} className="px-8 py-2 text-sm font-semibold text-purple-900">
                                        Total Staff: {service.positions.reduce((sum: number, p: any) => sum + (Number(p.quantity) || 1), 0)}
                                      </td>
                                      <td className="px-4 py-2 text-center text-sm font-semibold text-purple-900">
                                        {service.positions.reduce((sum: number, p: any) => sum + (Number(p.quantity) || 1) * (Number(p.duration) || 0), 0)}h
                                      </td>
                                      <td className="px-4 py-2 text-center text-sm text-orange-600">
                                        {(() => {
                                          const totalOtHrs = service.positions.reduce((sum: number, _: any, idx: number) => {
                                            const otKey = `${service.id}_${idx}`;
                                            return sum + (expoOvertimeHours.get(otKey) || 0);
                                          }, 0);
                                          return totalOtHrs > 0 ? `${totalOtHrs}h OT` : '';
                                        })()}
                                      </td>
                                      <td className="px-4 py-2 text-right text-sm font-bold text-purple-900">
                                        {formatCurrency(service.positions.reduce((sum: number, p: any, idx: number) => {
                                          const qty = Number(p.quantity) || 1;
                                          const dur = Number(p.duration) || 0;
                                          const baseRate = Number(p.hourlyRate) || Number(p.position?.hourlyRate) || 0;
                                          const otKey = `${service.id}_${idx}`;
                                          const otHours = expoOvertimeHours.get(otKey) || 0;
                                          const regularHours = Math.max(0, dur - otHours);
                                          const regularCost = qty * regularHours * baseRate;
                                          const otCost = qty * otHours * baseRate * 1.5;
                                          return sum + regularCost + otCost;
                                        }, 0))}
                                      </td>
                                    </tr>
                                  </>
                                )}

                                {/* Supplies Subgroup */}
                                {service.supplyItems && service.supplyItems.length > 0 && (
                                  <>
                                    <tr className="bg-blue-50">
                                      <td colSpan={6} className="px-4 py-2">
                                        <div className="flex items-center">
                                          <div className="w-4 h-4 mr-2">
                                            <svg viewBox="0 0 16 16" fill="currentColor" className="text-blue-600">
                                              <path d="M8 2a1 1 0 0 1 1 1v4h4a1 1 0 1 1 0 2H9v4a1 1 0 1 1-2 0V9H3a1 1 0 1 1 0-2h4V3a1 1 0 0 1 1-1z"/>
                                            </svg>
                                          </div>
                                          <span className="font-medium text-blue-800">Supplies & Materials</span>
                                        </div>
                                      </td>
                                    </tr>
                                    {service.supplyItems.map((supply: any, supplyIndex: number) => (
                                      <tr key={supplyIndex} className="bg-blue-25">
                                        <td className="px-8 py-2 text-sm">
                                          <div className="flex items-center">
                                            <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                                            {supply.isKit && supply.kit
                                              ? `${supply.kit.name} (Kit)`
                                              : supply.supply?.name || "Supply Item"
                                            }
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-center text-gray-400 text-sm">-</td>
                                        <td className="px-4 py-2 text-center text-gray-400 text-sm">-</td>
                                        <td className="px-4 py-2 text-center text-sm">
                                          Qty: {supply.quantity || 1}
                                        </td>
                                        <td className="px-4 py-2 text-center text-sm">
                                          {supply.isKit ? "kit" : supply.supply?.unit || "unit"}
                                        </td>
                                        <td className="px-4 py-2 text-right text-sm">
                                          {formatCurrency((supply.quantity || 1) * (supply.unitCost || 0))}
                                        </td>
                                      </tr>
                                    ))}
                                  </>
                                )}

                                {/* Add-on Services */}
                                {service.addOnServices && service.addOnServices.length > 0 && (
                                  <>
                                    <tr className="bg-green-50">
                                      <td colSpan={6} className="px-4 py-2">
                                        <div className="flex items-center">
                                          <div className="w-4 h-4 mr-2">
                                            <svg viewBox="0 0 16 16" fill="currentColor" className="text-green-600">
                                              <path d="M8 2a1 1 0 0 1 1 1v4h4a1 1 0 1 1 0 2H9v4a1 1 0 1 1-2 0V9H3a1 1 0 1 1 0-2h4V3a1 1 0 0 1 1-1z"/>
                                            </svg>
                                          </div>
                                          <span className="font-medium text-green-800">Additional Services</span>
                                        </div>
                                      </td>
                                    </tr>
                                    {service.addOnServices.map((addon: any, addonIndex: number) => {
                                      const addOnService = addOnServices?.find((aos: AddOnServiceDto) => aos.id === addon.id);
                                      let addOnCost = 0;
                                      let rateDisplay = "TBD";

                                      if (!isLoading) {
                                        // For Collection, Delivery, Site Service: add-ons are included
                                        if (
                                          service.serviceType === ServiceType.COLLECTION ||
                                          service.serviceType === ServiceType.DELIVERY ||
                                          service.serviceType === ServiceType.ON_SITE_SERVICE
                                        ) {
                                          addOnCost = 0;
                                          rateDisplay = "Included in service";
                                        }
                                        // For Warehouse and Fabrication: calculate using volume
                                        else if (
                                          (service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
                                           service.serviceType === ServiceType.FABRICATION_SERVICE) &&
                                          addOnService?.rate
                                        ) {
                                          const totalCubicFeet = service.inventories?.reduce((total: number, inventory: any) => {
                                            const volume = ((inventory.length || 1) * (inventory.width || 1) * (inventory.height || 1)) / 1728;
                                            return total + volume;
                                          }, 0) || 60;

                                          if (addOnService.unit === 'CU_FT') {
                                            const volumeRate = addOnService.rate * totalCubicFeet;
                                            const minimumRate = addOnService.minimumCharge || 0;
                                            addOnCost = Math.max(volumeRate, minimumRate);

                                            if (addOnCost === minimumRate && minimumRate > volumeRate) {
                                              rateDisplay = `$${addOnService.rate}/ft³ × ${totalCubicFeet.toFixed(1)} ft³ (Min: $${minimumRate})`;
                                            } else {
                                              rateDisplay = `$${addOnService.rate}/ft³ × ${totalCubicFeet.toFixed(1)} ft³`;
                                            }
                                          } else if (addOnService.unit === 'HOURLY') {
                                            addOnCost = addOnService.rate * totalHours;
                                            rateDisplay = `$${addOnService.rate}/hr × ${totalHours.toFixed(2)}h`;
                                          } else if (addOnService.unit === 'EACH') {
                                            addOnCost = addOnService.rate;
                                            rateDisplay = `$${addOnService.rate} each`;
                                          }
                                        }
                                      }

                                      return (
                                        <tr key={addonIndex} className="bg-green-25">
                                          <td className="px-8 py-2 text-sm">
                                            <div className="flex items-center">
                                              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                                              <div>
                                                <div className="flex items-center">
                                                  <span>{addon.name}</span>
                                                  {!isLoading && addOnCost === 0 && (
                                                    service.serviceType === ServiceType.COLLECTION ||
                                                    service.serviceType === ServiceType.DELIVERY ||
                                                    service.serviceType === ServiceType.ON_SITE_SERVICE
                                                  ) && (
                                                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                                      Included
                                                    </span>
                                                  )}
                                                </div>
                                                {!isLoading && addOnService && rateDisplay !== "TBD" && (
                                                  <div className="text-xs text-gray-500">
                                                    {rateDisplay}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 text-center text-gray-400 text-sm">-</td>
                                          <td className="px-4 py-2 text-center text-gray-400 text-sm">-</td>
                                          <td className="px-4 py-2 text-center text-gray-400 text-sm">-</td>
                                          <td className="px-4 py-2 text-center text-gray-400 text-sm">
                                            {(service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
                                              service.serviceType === ServiceType.FABRICATION_SERVICE) && !isLoading ? (
                                              <div className="text-xs">
                                                {service.inventories?.reduce((total: number, inventory: any) => {
                                                  const volume = ((inventory.length || 1) * (inventory.width || 1) * (inventory.height || 1)) / 1728;
                                                  return total + volume;
                                                }, 0)?.toFixed(1) || '60.0'} ft³
                                              </div>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-right text-sm">
                                            {isLoading ? (
                                              <Spin size="small" />
                                            ) : (
                                              <div className={addOnCost === 0 ? "text-green-600 font-medium" : ""}>
                                                {addOnCost === 0 ? "Included" : formatCurrency(addOnCost)}
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </>
                                )}
                              </tbody>
                                </table>
                              </div>

                              {/* Service Summary */}
                              {service.scope && (
                                <div className="mt-4 p-3 bg-gray-50 rounded">
                                  <div className="text-sm text-gray-600">
                                    <strong>Scope:</strong> {service.scope}
                                  </div>
                                </div>
                              )}

                              {/* Completion Notes - Only show for completed services */}
                              {service.status === 'COMPLETED' && (
                                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
                                  <h6 className="font-semibold text-green-900 mb-3 flex items-center">
                                    <CheckCircle size={16} className="mr-2" />
                                    Service Completion Details
                                  </h6>
                                  <div className="space-y-2 text-sm">
                                    {service.completedAt && (
                                      <div>
                                        <span className="text-gray-600 font-medium">Completed:</span>{' '}
                                        <span className="text-gray-900">
                                          {dayjs.utc(service.completedAt).format('MMM D, YYYY [at] h:mm A')}
                                        </span>
                                      </div>
                                    )}
                                    {service.timeIn && service.timeOut && (
                                      <div>
                                        <span className="text-gray-600 font-medium">Time Logged:</span>{' '}
                                        <span className="text-gray-900">
                                          {dayjs.utc(service.timeIn).format('h:mm A')} - {dayjs.utc(service.timeOut).format('h:mm A')}
                                          {' '}({dayjs.utc(service.timeOut).diff(dayjs.utc(service.timeIn), 'hour', true).toFixed(1)}h)
                                        </span>
                                      </div>
                                    )}
                                    {service.staffs && service.staffs.length > 0 && (
                                      <div>
                                        <span className="text-gray-600 font-medium">Staff Assigned:</span>
                                        <div className="mt-1 flex flex-wrap gap-2">
                                          {service.staffs.slice(0, 6).map((staffMember: any, idx: number) => (
                                            <div
                                              key={idx}
                                              className="px-2 py-1 bg-white border border-green-200 rounded-full text-xs font-medium text-gray-700"
                                            >
                                              {getClientName(staffMember.user)}
                                            </div>
                                          ))}
                                          {service.staffs.length > 6 && (
                                            <div className="px-2 py-1 bg-white border border-green-200 rounded-full text-xs font-medium text-gray-500">
                                              +{service.staffs.length - 6} more
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {service.completionNotes && (
                                      <div>
                                        <span className="text-gray-600 font-medium">Notes:</span>
                                        <div className="mt-1 p-2 bg-white rounded border border-green-200">
                                          {service.completionNotes}
                                        </div>
                                      </div>
                                    )}
                                    {service.clientSignature && (
                                      <div className="flex items-center text-green-700">
                                        <CheckCircle size={14} className="mr-1" />
                                        <span>Client signature received</span>
                                      </div>
                                    )}
                                    {service.staffCheckoff && (
                                      <div className="flex items-center text-green-700">
                                        <CheckCircle size={14} className="mr-1" />
                                        <span>Staff check-off completed</span>
                                      </div>
                                    )}
                                    {!service.completionNotes && !service.clientSignature && !service.staffCheckoff && !service.timeIn && !service.staffs?.length && (
                                      <div className="text-gray-500 italic">
                                        No additional completion details recorded
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                            </div>
                          )}
                        </div>
                          );
                        })}
                      </Fragment>
                    ))}

                    {/* Additional Charges Section */}
                    <div className="border-t bg-white">
                      <div className="p-4 bg-purple-50 border-b">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-gray-900">Additional Charges</h4>
                            <p className="text-sm text-gray-600">Add supplies or custom line items (shipping, handling, etc.)</p>
                          </div>
                          <div className="flex gap-2">
                            <Select
                              placeholder="Add supply..."
                              showSearch
                              optionFilterProp="children"
                              className="w-48"
                              value={undefined}
                              onChange={(supplyId) => {
                                const supply = supplies.find((s: any) => s.id === supplyId) as any;
                                if (supply) {
                                  const newCharge: typeof additionalCharges[0] = {
                                    id: uuidv4(),
                                    type: 'supply',
                                    description: supply.name,
                                    quantity: 1,
                                    rate: Number(supply.billableRate) || Number(supply.price) || Number(supply.cost) || 0,
                                    supplyId: supply.id,
                                  };
                                  setAdditionalCharges([...additionalCharges, newCharge]);
                                }
                              }}
                              options={supplies.map((s: any) => ({
                                value: s.id,
                                label: `${s.name} - $${(Number(s.billableRate) || Number(s.price) || Number(s.cost) || 0).toFixed(2)}`,
                              }))}
                            />
                            <Button
                              type="dashed"
                              icon={<Plus size={14} />}
                              onClick={() => {
                                const newCharge: typeof additionalCharges[0] = {
                                  id: uuidv4(),
                                  type: 'custom',
                                  description: '',
                                  date: dayjs().format('YYYY-MM-DD'),
                                  quantity: 1,
                                  rate: 0,
                                };
                                setAdditionalCharges([...additionalCharges, newCharge]);
                              }}
                            >
                              Custom Item
                            </Button>
                          </div>
                        </div>
                      </div>
                      {/* Billable Task Charges (auto-generated, read-only) */}
                      {billableTaskCharges.length > 0 && (
                        <div className="p-4 bg-amber-50 border-b">
                          <div className="text-xs font-medium text-amber-700 mb-2">Billable Tasks (auto-generated)</div>
                          <div className="space-y-2">
                            {billableTaskCharges.map((charge) => (
                              <div key={charge.id} className="flex justify-between items-center p-2 bg-white rounded border border-amber-200 text-sm">
                                <div>
                                  <span className="font-medium">{charge.description}</span>
                                  {charge.notes && <span className="text-gray-500 ml-2">- {charge.notes}</span>}
                                </div>
                                <div className="text-right whitespace-nowrap">
                                  <span className="text-gray-500">{charge.quantity}h x {formatCurrency(charge.rate)}</span>
                                  <span className="font-medium ml-3">{formatCurrency(charge.quantity * charge.rate)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {additionalCharges.filter((c) => !c.id.startsWith('task-')).length > 0 && (
                        <div className="p-4">
                          <div className="space-y-3">
                            {additionalCharges.filter((c) => !c.id.startsWith('task-')).map((charge, index) => (
                              <div key={charge.id} className="p-3 border rounded bg-gray-50">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 pt-2">
                                    {charge.type === 'supply' ? (
                                      <Package size={16} className="text-purple-600" />
                                    ) : (
                                      <span className="text-gray-400 text-sm">#{index + 1}</span>
                                    )}
                                  </div>
                                  <div className="flex-1 grid grid-cols-12 gap-3 items-center">
                                    {/* Description */}
                                    <div className="col-span-3">
                                      <Input
                                        placeholder="Item name"
                                        value={charge.description}
                                        onChange={(e) => {
                                          const updated = [...additionalCharges];
                                          updated[index] = { ...charge, description: e.target.value };
                                          setAdditionalCharges(updated);
                                        }}
                                        disabled={charge.type === 'supply'}
                                      />
                                    </div>
                                    {/* Date */}
                                    <div className="col-span-2">
                                      <DatePicker
                                        value={charge.date ? dayjs(charge.date) : null}
                                        onChange={(date) => {
                                          const updated = [...additionalCharges];
                                          updated[index] = { ...charge, date: date?.format('YYYY-MM-DD') };
                                          setAdditionalCharges(updated);
                                        }}
                                        placeholder="Date"
                                        className="w-full"
                                        format="MM/DD/YY"
                                      />
                                    </div>
                                    {/* Quantity */}
                                    <div className="col-span-2">
                                      <InputNumber
                                        min={1}
                                        value={charge.quantity}
                                        onChange={(value) => {
                                          const updated = [...additionalCharges];
                                          updated[index] = { ...charge, quantity: value || 1 };
                                          setAdditionalCharges(updated);
                                        }}
                                        placeholder="Qty"
                                        className="w-full"
                                      />
                                    </div>
                                    {/* Rate */}
                                    <div className="col-span-2">
                                      <InputNumber
                                        min={0}
                                        step={0.01}
                                        value={charge.rate}
                                        onChange={(value) => {
                                          const updated = [...additionalCharges];
                                          updated[index] = { ...charge, rate: value || 0 };
                                          setAdditionalCharges(updated);
                                        }}
                                        placeholder="Rate"
                                        prefix="$"
                                        className="w-full"
                                      />
                                    </div>
                                    {/* Total */}
                                    <div className="col-span-3 flex items-center justify-between">
                                      <span className="font-medium">
                                        {formatCurrency(charge.quantity * charge.rate)}
                                      </span>
                                      <button
                                        onClick={() => {
                                          setAdditionalCharges(additionalCharges.filter((_, i) => i !== index));
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                {/* Notes/Description row */}
                                <div className="mt-2 ml-7">
                                  <Input
                                    placeholder="Description / notes (appears on invoice)"
                                    value={charge.notes || ''}
                                    onChange={(e) => {
                                      const updated = [...additionalCharges];
                                      updated[index] = { ...charge, notes: e.target.value };
                                      setAdditionalCharges(updated);
                                    }}
                                    className="text-sm"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 pt-3 border-t flex justify-end">
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Additional Charges Subtotal</div>
                              <div className="text-lg font-semibold text-purple-700">
                                {formatCurrency(overallTotals.additionalChargesTotal)}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Storage Estimate Section */}
                    {storageEstimate.itemCount > 0 && (
                      <div className="border-t bg-white">
                        <div
                          className="p-4 bg-blue-50 border-b cursor-pointer select-none"
                          onClick={() => setStorageExpanded(!storageExpanded)}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={includeStorage}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setIncludeStorage(e.target.checked);
                                  if (e.target.checked) setStorageExpanded(true);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4"
                              />
                              {storageExpanded ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                              <div>
                                <h4 className="font-medium text-gray-900">Storage Estimate</h4>
                                <p className="text-sm text-gray-600">
                                  {storageEstimate.itemCount} item{storageEstimate.itemCount !== 1 ? 's' : ''} · {storageEstimate.totalCubicFeet.toFixed(1)} ft³
                                  {selectedStorageLocation ? ` · ${selectedStorageLocation.name}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className={`text-lg font-semibold ${includeStorage ? 'text-blue-700' : 'text-gray-400'}`}>
                              {formatCurrency(storageEstimate.monthlyCharge)}
                            </div>
                          </div>
                        </div>
                        {storageExpanded && (
                          <div className="p-4">
                            <div className="grid grid-cols-5 gap-4 text-sm items-end">
                              <div>
                                <div className="text-gray-500 mb-1">Volume</div>
                                <div className="font-medium">{storageEstimate.totalCubicFeet.toFixed(2)} ft³</div>
                                <div className="text-xs text-gray-400">incl. 10% buffer</div>
                              </div>
                              <div>
                                <div className="text-gray-500 mb-1">Min. Cubic Feet</div>
                                <InputNumber
                                  min={0}
                                  step={1}
                                  value={storageMinCubicFeet}
                                  onChange={(value) => setStorageMinCubicFeet(value ?? 100)}
                                  className="w-full"
                                  size="small"
                                  suffix="ft³"
                                />
                              </div>
                              <div>
                                <div className="text-gray-500 mb-1">Billable ft³</div>
                                <div className="font-medium">{storageEstimate.billableCubicFeet.toFixed(2)} ft³</div>
                              </div>
                              <div>
                                <div className="text-gray-500 mb-1">Location / Rate</div>
                                <Select
                                  placeholder="Select location..."
                                  value={selectedStorageLocationId}
                                  onChange={(value) => setSelectedStorageLocationId(value)}
                                  className="w-full"
                                  size="small"
                                  allowClear
                                  showSearch
                                  optionFilterProp="label"
                                  options={storageLocations.map(loc => ({
                                    value: loc.id,
                                    label: `${loc.name} — $${loc.rate.toFixed(2)}/ft³`,
                                  }))}
                                />
                              </div>
                              <div>
                                <div className="text-gray-500 mb-1">Est. Monthly Charge</div>
                                <div className={`text-lg font-semibold ${includeStorage ? 'text-blue-700' : 'text-gray-400'}`}>
                                  {formatCurrency(storageEstimate.monthlyCharge)}
                                </div>
                                {storageEstimate.rate > 0 && (
                                  <div className="text-xs text-gray-400">
                                    {storageEstimate.billableCubicFeet.toFixed(1)} ft³ × ${storageEstimate.rate.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Admin Charges Section */}
                    {adminCharges.length > 0 && (
                      <div className="border-t bg-white">
                        <div className="p-4 bg-gray-50 border-b">
                          <h4 className="font-medium text-gray-900">Admin Charges</h4>
                          <p className="text-sm text-gray-600">Select additional administrative charges to include</p>
                        </div>
                        <div className="p-4">
                          <div className="space-y-2">
                            {adminCharges.map(charge => {
                              const isSelected = selectedAdminCharges.has(charge.id);
                              const quantity = selectedAdminCharges.get(charge.id)?.quantity || 1;
                              const lineTotal = (charge.rate || 0) * quantity;

                              return (
                                <div key={charge.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                                  <div className="flex items-center space-x-3 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        const newSelected = new Map(selectedAdminCharges);
                                        if (e.target.checked) {
                                          newSelected.set(charge.id, { chargeId: charge.id, quantity: 1 });
                                        } else {
                                          newSelected.delete(charge.id);
                                        }
                                        setSelectedAdminCharges(newSelected);
                                      }}
                                      className="w-4 h-4"
                                    />
                                    <div className="flex-1">
                                      <div className="font-medium">{charge.name}</div>
                                      <div className="text-sm text-gray-500">
                                        ${(charge.rate || 0).toFixed(2)} / {charge.unit}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-4">
                                    {isSelected && (
                                      <>
                                        <Input
                                          type="number"
                                          min={1}
                                          value={quantity}
                                          onChange={(e) => {
                                            const newSelected = new Map(selectedAdminCharges);
                                            newSelected.set(charge.id, {
                                              chargeId: charge.id,
                                              quantity: parseInt(e.target.value) || 1
                                            });
                                            setSelectedAdminCharges(newSelected);
                                          }}
                                          className="w-20"
                                        />
                                        <span className="font-medium min-w-[80px] text-right">
                                          {formatCurrency(lineTotal)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {selectedAdminCharges.size > 0 && (
                            <div className="mt-4 pt-3 border-t flex justify-end">
                              <div className="text-right">
                                <div className="text-sm text-gray-500">Admin Charges Subtotal</div>
                                <div className="text-lg font-semibold">
                                  {formatCurrency(overallTotals.adminChargesTotal)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Overall Totals */}
                    <div className="p-6 bg-gray-50">
                      <div className="flex justify-end">
                        <div className="w-96">
                          <div className="flex justify-between py-2">
                            <span>Subtotal:</span>
                            <span>
                              {isLoading ? (
                                <div className="flex items-center">
                                  <Spin size="small" className="mr-2" />
                                  <span className="text-gray-400">Calculating...</span>
                                </div>
                              ) : (
                                formatCurrency(overallTotals.subtotal)
                              )}
                            </span>
                          </div>
                          {overallTotals.storageTotal > 0 && (
                            <div className="flex justify-between py-1 text-sm text-blue-600">
                              <span>Storage ({storageEstimate.billableCubicFeet.toFixed(1)} ft³ × {formatCurrency(storageEstimate.rate)}):</span>
                              <span>{formatCurrency(overallTotals.storageTotal)}</span>
                            </div>
                          )}
                          {overallTotals.discountAmount !== 0 && (
                            <div className={`flex justify-between py-2 ${overallTotals.discountPercentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <span>
                                {overallTotals.discountPercentage > 0 ? 'Discount' : 'Premium'} ({overallTotals.discountPercentage > 0 ? '+' : ''}{overallTotals.discountPercentage}%):
                              </span>
                              <span>
                                {isLoading ? (
                                  <div className="flex items-center">
                                    <Spin size="small" className="mr-2" />
                                    <span className="text-gray-400">Calculating...</span>
                                  </div>
                                ) : (
                                  overallTotals.discountPercentage > 0 
                                    ? `-${formatCurrency(Math.abs(overallTotals.discountAmount))}`
                                    : `+${formatCurrency(Math.abs(overallTotals.discountAmount))}`
                                )}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between py-3 border-t text-lg font-bold">
                            <span>Total Estimate:</span>
                            <span>
                              {isLoading ? (
                                <div className="flex items-center">
                                  <Spin size="small" className="mr-2" />
                                  <span className="text-gray-400">Calculating...</span>
                                </div>
                              ) : (
                                formatCurrency(overallTotals.total)
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    No services found for this booking.
                  </div>
                )}
              </div>
            ),
          },
          {
            key: "invoice",
            label: "Invoice",
            children: (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                    <div>
                      <h4 className="font-medium text-lg">Create Invoice</h4>
                    <p className="text-gray-600 text-sm">Configure payment terms and generate invoice from selected services</p>
                  </div>
                </div>

                {/* Existing Invoices Section */}
                {existingInvoices && existingInvoices.length > 0 && (
                  <div className="p-6 border-b bg-blue-50">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="mr-2">Existing Invoices</span>
                      <Tag color="blue">{existingInvoices.length}</Tag>
                    </h5>
                    <div className="space-y-2">
                      {existingInvoices.map((invoice: any) => {
                        const getStatusColor = (status: string) => {
                          switch (status) {
                            case 'draft': return 'default';
                            case 'pending': return 'processing';
                            case 'sent': return 'warning';
                            case 'paid': return 'success';
                            case 'overdue': return 'error';
                            case 'cancelled': return 'default';
                            default: return 'default';
                          }
                        };

                        const getStatusText = (status: string) => {
                          return status.charAt(0).toUpperCase() + status.slice(1);
                        };

                        return (
                          <div
                            key={invoice.id}
                            className="flex items-center justify-between p-3 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors"
                          >
                            <div className="flex items-center space-x-4">
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {invoice.invoiceNumber}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {dayjs(invoice.invoiceDate || invoice.createdAt).format('MMM D, YYYY')}
                                  {invoice.dueDate && ` • Due: ${dayjs(invoice.dueDate).format('MMM D, YYYY')}`}
                                </div>
                              </div>
                              <Tag color={getStatusColor(invoice.status)}>
                                {getStatusText(invoice.status)}
                              </Tag>
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">{formatCurrency(invoice.total)}</span>
                                {invoice.paid > 0 && (
                                  <span className="text-gray-500 ml-2">
                                    (Paid: {formatCurrency(invoice.paid)})
                                  </span>
                                )}
                              </div>
                    </div>
                    <div className="flex items-center space-x-2">
                              <Button
                                type="link"
                                onClick={async () => {
                                  try {
                                    // Fetch full invoice data
                                    const invoiceData = await getInvoice(invoice.id);
                                    
                                    // Format invoice data for printing
                                    const printData = {
                                      invoiceNumber: invoiceData.invoiceNumber,
                                      invoiceDate: invoiceData.invoiceDate || invoiceData.createdAt,
                                      dueDate: invoiceData.dueDate || '',
                                      booking: {
                                        referenceNumber: booking?.referenceNumber,
                                        project: booking?.project,
                                      },
                                      client: invoiceData.client || booking?.client || {},
                                      services: (invoiceData.serviceTickets || []).map((st: any) => ({
                                        serial: st.serial || '',
                                        serviceType: st.serviceType || '',
                                        serviceDatetime: st.serviceDatetime || '',
                                        client: st.client,
                                        location: st.shippingAddress || {},
                                        scope: st.scope || '',
                                        duration: st.duration || 0,
                                        travelTime: st.travelTime || 0,
                                        addOnServices: [],
                                        supplies: [],
                                        packageCount: 0,
                                        originalCost: 0,
                                        finalCost: Number(st.lineItems?.[0]?.amount || invoiceData.total / (invoiceData.serviceTickets?.length || 1)),
                                        isOverridden: false,
                                      })),
                                      adminCharges: [],
                                      additionalCharges: (invoiceData.lineItems || []).filter((item: any) => !item.serviceTicketId).map((item: any) => ({
                                        description: item.description || '',
                                        notes: item.notes || '',
                                        date: item.date || '',
                                        quantity: item.quantity || 1,
                                        rate: item.rate || item.amount || 0,
                                        total: item.amount || 0,
                                        type: 'custom' as const,
                                      })),
                                      financial: {
                                        subtotal: Number(invoiceData.subtotal || invoiceData.total),
                                        depositAmount: Number(invoiceData.metadata?.depositAmount || 0),
                                        balanceDue: Number(invoiceData.balance || invoiceData.total),
                                        netTerms: invoiceData.metadata?.netTerms ?? 0,
                                        paymentTerms: invoiceData.metadata?.paymentTerms || (invoiceData.metadata?.netTerms === 0 ? 'Due on Receipt' : invoiceData.metadata?.netTerms === -1 ? 'Due Before Delivery' : invoiceData.metadata?.netTerms ? `Net ${invoiceData.metadata.netTerms} Days` : 'Due on Receipt'),
                                      },
                                    };
                                    
                                    setInvoiceToPrint(printData);
                                    
                                    // Wait for state to update, then trigger print
                                    setTimeout(() => {
                                      handlePrintExistingInvoice();
                                    }, 100);
                                  } catch (error: any) {
                                    message.error('Failed to prepare invoice for printing');
                                    console.error('Print error:', error);
                                  }
                                }}
                                className="text-blue-600"
                              >
                                Print Invoice
                              </Button>
                              <Button
                                type="link"
                                onClick={() => window.open(`/billing/invoicing`, '_blank')}
                                className="text-blue-600"
                              >
                                View Invoice
                              </Button>
                    </div>
                  </div>
                        );
                      })}
                </div>
                  </div>
                )}

                {selectedServices.size > 0 ? (
                  <div className="p-6 space-y-6">
                    {/* Invoice Input Boxes - Top */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Deposit Input */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Deposit to Confirm Service
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-700 font-medium">$</span>
                          <InputNumber
                            min={0}
                            step={0.01}
                            value={requestedDeposit}
                            onChange={(value) => setRequestedDeposit(value || 0)}
                            placeholder="0.00"
                            className="flex-1"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value?.replace(/\$\s?|(,*)/g, '') as any}
                            size="large"
                          />
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                          Due immediately to confirm service
                        </div>
                      </div>

                      {/* Net Terms */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Payment Terms
                        </label>
                        <Select
                          value={netTerms}
                          onChange={(value) => {
                            setNetTerms(value);
                            if (dueDate) {
                              setDueDate('');
                            }
                          }}
                          className="w-full"
                          size="large"
                          options={[
                            { label: 'Due Before Delivery', value: -1 },
                            { label: 'Due on Receipt', value: 0 },
                            { label: 'Net 15 Days', value: 15 },
                            { label: 'Net 30 Days', value: 30 },
                            { label: 'Net 60 Days', value: 60 },
                            { label: 'Net 90 Days', value: 90 },
                          ]}
                        />
                        <div className="mt-2 text-xs text-gray-600">
                          Payment terms for remaining balance
                        </div>
                      </div>

                      {/* Due Date Override */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Final Invoice Due Date
                        </label>
                        <DatePicker
                          value={dueDate ? dayjs(dueDate) : null}
                          onChange={(date) => setDueDate(date ? date.format('YYYY-MM-DD') : '')}
                          placeholder={finalDueDate?.format('MM/DD/YYYY') || 'Before Delivery'}
                          className="w-full"
                          format="MM/DD/YYYY"
                          disabled={netTerms === -1 && !dueDate}
                          size="large"
                        />
                        <div className="mt-2 text-xs text-gray-600">
                          {dueDate
                            ? 'Custom due date set'
                            : netTerms === -1
                              ? 'Due Before Delivery (no specific date)'
                              : `Auto: ${finalDueDate?.format('MM/DD/YYYY')} (${netTerms === 0 ? 'Due on receipt' : `Net ${netTerms}`})`
                          }
                        </div>
                      </div>
                    </div>

                    {/* Services Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Service date</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Reference</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Service</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Client</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Location</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-900">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {services
                            .filter(service => selectedServices.has(service.id))
                            .map((service) => {
                              const costBreakdown = calculateServiceCost(service);
                              const packageCount = service.inventories?.length || service.estimatedItemCount || service.estimateTemplates?.reduce((sum, t) => sum + (t.quantity || 0), 0) || 0;

                              // Get location (city, state) - check tempLocationData as fallback
                              const location = service.location;
                              const tempLoc = service.tempLocationData;
                              const locationDisplay = location?.city || tempLoc?.city
                                ? `${location?.city || tempLoc?.city || 'Unknown'}, ${location?.state || tempLoc?.state || ''}`
                                : 'Location TBD';

                              // Prepare detailed information sections
                              const scope = service.scope;
                              const addOnsUsed = service.addOnServices?.map(addon => addon.name).join(', ') || '';
                              const suppliesUsed = service.supplyItems?.map(supply => supply.supply?.name).join(', ') || '';
                              const projectName = booking?.project?.name;

                              return (
                                <Fragment key={service.id}>
                                  <tr>
                                    <td className="px-4 py-3 text-sm">
                                      {dayjs(service.serviceDatetime || service.startDatetime).format("ddd, MMM D, YYYY")}
                                    </td>
                                    <td className="px-4 py-3 text-sm">{service.serial}</td>
                                    <td className="px-4 py-3 text-sm">
                                      <div className="flex items-center gap-1">
                                        {service.isExpoDay && service.dayLabel ? (
                                          <span>{service.dayLabel}</span>
                                        ) : (
                                          capitalize(service.serviceType).replace(' Service', '')
                                        )}
                                        {service.isExpoDay && (
                                          <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">EXPO</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {service.tempLocationData?.name || service.client?.companyName || booking?.tempClientData?.name || "Quick Client"}
                                    </td>
                                    <td className="px-4 py-3 text-sm">{locationDisplay}</td>
                                    <td className="px-4 py-3 text-right text-sm font-medium">
                                      {formatCurrency(costBreakdown.total)}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td colSpan={6} className="px-4 py-3 text-xs text-gray-600 bg-gray-50">
                                      <div className="space-y-1">
                                        {scope && (
                                          <div><strong>Scope:</strong> {scope}</div>
                                        )}
                                        <div>
                                          {[
                                            addOnsUsed && `Add-ons: ${addOnsUsed}`,
                                            suppliesUsed && `Supplies: ${suppliesUsed}`,
                                            `Packages: ${packageCount}`,
                                            projectName && `Project: ${projectName}`
                                          ].filter(Boolean).join(' • ')}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                  {/* Expo Positions Breakdown in Invoice */}
                                  {service.isExpoDay && service.positions && service.positions.length > 0 && (
                                    <tr>
                                      <td colSpan={6} className="px-4 py-2 bg-purple-50">
                                        <div className="space-y-2">
                                          <div className="font-medium text-purple-800 text-xs">Labor Breakdown</div>
                                          <table className="w-full text-xs">
                                            <thead>
                                              <tr className="text-purple-700">
                                                <th className="text-left py-1">Position</th>
                                                <th className="text-center py-1">Staff</th>
                                                <th className="text-center py-1">Reg Hrs</th>
                                                <th className="text-center py-1">OT Hrs</th>
                                                <th className="text-center py-1">Total Hrs</th>
                                                <th className="text-right py-1">Amount</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {service.positions.map((pos: any, posIdx: number) => {
                                                const posQty = Number(pos.quantity) || 1;
                                                const posDuration = Number(pos.duration) || 0;
                                                const baseRate = Number(pos.hourlyRate) || Number(pos.position?.hourlyRate) || 0;
                                                const otKey = `${service.id}_${posIdx}`;
                                                const otHours = expoOvertimeHours.get(otKey) || 0;
                                                const regularHours = Math.max(0, posDuration - otHours);
                                                const regularCost = posQty * regularHours * baseRate;
                                                const otCost = posQty * otHours * baseRate * 1.5;
                                                const posSubtotal = regularCost + otCost;
                                                const totalHours = posQty * posDuration;

                                                return (
                                                  <tr key={posIdx} className="border-t border-purple-100">
                                                    <td className="py-1">
                                                      <div>{pos.position?.name || 'Unknown'}</div>
                                                      <div className="text-gray-400 text-[10px]">
                                                        {formatCurrency(baseRate)}/hr
                                                        {otHours > 0 && <span className="text-orange-500"> • OT: {formatCurrency(baseRate * 1.5)}/hr</span>}
                                                      </div>
                                                    </td>
                                                    <td className="text-center py-1">{posQty}</td>
                                                    <td className="text-center py-1">{regularHours}h</td>
                                                    <td className="text-center py-1">
                                                      {otHours > 0 ? <span className="text-orange-600 font-medium">{otHours}h</span> : '-'}
                                                    </td>
                                                    <td className="text-center py-1">{totalHours}h</td>
                                                    <td className="text-right py-1 font-medium">
                                                      <div>{formatCurrency(posSubtotal)}</div>
                                                      {otHours > 0 && (
                                                        <div className="text-[10px] text-gray-400">
                                                          {formatCurrency(regularCost)} + <span className="text-orange-500">{formatCurrency(otCost)}</span>
                                                        </div>
                                                      )}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                              <tr className="border-t border-purple-200 font-semibold text-purple-900">
                                                <td className="py-1">
                                                  Total: {service.positions.reduce((sum: number, p: any) => sum + (Number(p.quantity) || 1), 0)} staff
                                                </td>
                                                <td></td>
                                                <td className="text-center py-1">
                                                  {service.positions.reduce((sum: number, p: any, idx: number) => {
                                                    const dur = Number(p.duration) || 0;
                                                    const otKey = `${service.id}_${idx}`;
                                                    const otHours = expoOvertimeHours.get(otKey) || 0;
                                                    return sum + Math.max(0, dur - otHours);
                                                  }, 0)}h
                                                </td>
                                                <td className="text-center py-1 text-orange-600">
                                                  {(() => {
                                                    const totalOtHrs = service.positions.reduce((sum: number, _: any, idx: number) => {
                                                      const otKey = `${service.id}_${idx}`;
                                                      return sum + (expoOvertimeHours.get(otKey) || 0);
                                                    }, 0);
                                                    return totalOtHrs > 0 ? `${totalOtHrs}h` : '-';
                                                  })()}
                                                </td>
                                                <td className="text-center py-1">
                                                  {service.positions.reduce((sum: number, p: any) => sum + (Number(p.quantity) || 1) * (Number(p.duration) || 0), 0)}h
                                                </td>
                                                <td className="text-right py-1">
                                                  {formatCurrency(service.positions.reduce((sum: number, p: any, idx: number) => {
                                                    const qty = Number(p.quantity) || 1;
                                                    const dur = Number(p.duration) || 0;
                                                    const baseRate = Number(p.hourlyRate) || Number(p.position?.hourlyRate) || 0;
                                                    const otKey = `${service.id}_${idx}`;
                                                    const otHours = expoOvertimeHours.get(otKey) || 0;
                                                    const regularHours = Math.max(0, dur - otHours);
                                                    const regularCost = qty * regularHours * baseRate;
                                                    const otCost = qty * otHours * baseRate * 1.5;
                                                    return sum + regularCost + otCost;
                                                  }, 0))}
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}

                          {/* Admin Charges in Invoice */}
                          {selectedAdminCharges.size > 0 && (
                            <>
                              <tr className="bg-gray-100">
                                <td colSpan={6} className="px-4 py-2">
                                  <div className="font-medium text-gray-900">Admin Charges</div>
                                </td>
                              </tr>
                              {Array.from(selectedAdminCharges.entries()).map(([chargeId, chargeData]) => {
                                const charge = adminCharges.find(c => c.id === chargeData.chargeId);
                                if (!charge) return null;

                                const lineTotal = (charge.rate || 0) * chargeData.quantity;

                                return (
                                  <tr key={chargeId} className="bg-gray-50">
                                    <td className="px-4 py-2 text-sm" colSpan={2}>
                                      <div className="flex items-center">
                                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                        {charge.name}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-500" colSpan={3}>
                                      ${(charge.rate || 0).toFixed(2)} / {charge.unit} × {chargeData.quantity}
                                    </td>
                                    <td className="px-4 py-2 text-right text-sm font-medium">
                                      {formatCurrency(lineTotal)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          )}

                          {/* Additional Charges in Invoice (includes billable tasks) */}
                          {allAdditionalCharges.length > 0 && (
                            <>
                              <tr className="bg-purple-50">
                                <td colSpan={6} className="px-4 py-2">
                                  <div className="font-medium text-purple-900">Additional Charges</div>
                                </td>
                              </tr>
                              {allAdditionalCharges.map((charge) => {
                                const lineTotal = charge.rate * charge.quantity;

                                return (
                                  <tr key={charge.id} className="bg-purple-50/50">
                                    <td className="px-4 py-2 text-sm">
                                      {charge.date ? dayjs(charge.date).format("ddd, MMM D, YYYY") : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm" colSpan={2}>
                                      <div className="flex items-center">
                                        <Package size={12} className="text-purple-600 mr-2" />
                                        {charge.description}
                                      </div>
                                      {charge.notes && (
                                        <div className="text-xs text-gray-500 ml-5">{charge.notes}</div>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-500" colSpan={2}>
                                      ${charge.rate.toFixed(2)} × {charge.quantity}
                                    </td>
                                    <td className="px-4 py-2 text-right text-sm font-medium">
                                      {formatCurrency(lineTotal)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          )}

                          {/* Storage Estimate in Invoice */}
                          {includeStorage && storageEstimate.monthlyCharge > 0 && (
                            <>
                              <tr className="bg-blue-50">
                                <td colSpan={6} className="px-4 py-2">
                                  <div className="font-medium text-blue-900">Storage</div>
                                </td>
                              </tr>
                              <tr className="bg-blue-50/50">
                                <td className="px-4 py-2 text-sm" colSpan={2}>
                                  <div>Monthly Storage{selectedStorageLocation ? ` — ${selectedStorageLocation.name}` : ''}</div>
                                  <div className="text-xs text-gray-500">
                                    {storageEstimate.itemCount} item{storageEstimate.itemCount !== 1 ? 's' : ''} · {storageEstimate.totalCubicFeet.toFixed(2)} ft³ volume
                                    {storageEstimate.billableCubicFeet > storageEstimate.totalCubicFeet && ` (${storageMinCubicFeet} ft³ minimum applied)`}
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500" colSpan={3}>
                                  {storageEstimate.billableCubicFeet.toFixed(2)} ft³ × ${storageEstimate.rate.toFixed(2)}/ft³
                                </td>
                                <td className="px-4 py-2 text-right text-sm font-medium">
                                  {formatCurrency(storageEstimate.monthlyCharge)}
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan={5} className="px-4 py-3 text-right font-medium text-sm">
                              Invoice Total:
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-lg">
                              {formatCurrency(
                                services
                                  .filter(service => selectedServices.has(service.id))
                                  .reduce((total, service) => {
                                    const cost = calculateServiceCost(service);
                                    return total + cost.total;
                                  }, 0) + overallTotals.adminChargesTotal + overallTotals.additionalChargesTotal + overallTotals.storageTotal
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Invoice Summary and Actions - Below Table */}
                    <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-6">
                      {/* Invoice Summary */}
                      <div className="mb-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700 font-medium">Total Invoice Amount:</span>
                            <span className="font-semibold text-gray-900 text-lg">
                              {formatCurrency(
                                services
                                  .filter(service => selectedServices.has(service.id))
                                  .reduce((total, service) => {
                                    const cost = calculateServiceCost(service);
                                    return total + cost.total;
                                  }, 0) + overallTotals.adminChargesTotal + overallTotals.additionalChargesTotal + overallTotals.storageTotal
                              )}
                            </span>
                          </div>

                          {requestedDeposit > 0 ? (
                            <>
                              <div className="flex justify-between items-center pt-2 border-t border-green-300">
                                <span className="text-gray-700 font-medium">Balance Due Now (Deposit):</span>
                                <span className="font-bold text-red-600 text-lg">
                                  {formatCurrency(requestedDeposit)}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600">
                                Required to confirm service booking
                              </div>

                              <div className="flex justify-between items-center pt-2 border-t border-green-300">
                                <div>
                                  <span className="text-gray-700 font-medium">Final Balance Due:</span>
                                  <div className="text-xs text-gray-600">
                                    Due {dueDate ? dayjs(dueDate).format('MM/DD/YYYY') : (finalDueDate?.format('MM/DD/YYYY') || 'Before Delivery')}
                                  </div>
                                </div>
                                <span className="font-semibold text-gray-900 text-lg">
                                  {formatCurrency(
                                    services
                                      .filter(service => selectedServices.has(service.id))
                                      .reduce((total, service) => {
                                        const cost = calculateServiceCost(service);
                                        return total + cost.total;
                                      }, 0) + overallTotals.adminChargesTotal + overallTotals.additionalChargesTotal + overallTotals.storageTotal - requestedDeposit
                                  )}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between items-center pt-2 border-t border-green-300">
                              <div>
                                <span className="text-gray-700 font-medium">Full Amount Due:</span>
                                <div className="text-xs text-gray-600">
                                  Due {dueDate ? dayjs(dueDate).format('MM/DD/YYYY') : (finalDueDate?.format('MM/DD/YYYY') || 'Before Delivery')}
                                </div>
                              </div>
                              <span className="font-bold text-red-600 text-lg">
                                {formatCurrency(
                                  services
                                    .filter(service => selectedServices.has(service.id))
                                    .reduce((total, service) => {
                                      const cost = calculateServiceCost(service);
                                      return total + cost.total;
                                    }, 0) + overallTotals.adminChargesTotal + overallTotals.additionalChargesTotal + overallTotals.storageTotal
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          type="default"
                          size="large"
                          onClick={() => handleSaveInvoiceConfig()}
                            className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium"
                        >
                          Save Configuration
                        </Button>
                        <Button
                          type="primary"
                          size="large"
                            onClick={handleSaveInvoiceClick}
                            className="flex-1 bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700 font-medium"
                        >
                          Save Invoice
                        </Button>
                        <Button
                          size="large"
                          onClick={handleGenerateInvoice}
                            className="flex-1 border-gray-400 text-gray-700 hover:border-gray-500 hover:text-gray-800 font-medium"
                        >
                          Generate Invoice (Print/PDF)
                        </Button>
                      </div>
                        <div className="flex flex-col sm:flex-row gap-3 text-xs text-gray-600">
                          <div className="flex-1">
                            <strong>Save Configuration:</strong> Saves payment terms and settings without creating an invoice
                          </div>
                          <div className="flex-1">
                            <strong>Save Invoice:</strong> Creates a draft invoice that can be sent to QuickBooks or emailed to client
                          </div>
                          <div className="flex-1">
                            <strong>Generate Invoice:</strong> For analog print/send - generates a PDF for printing or emailing directly
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p className="mb-2">No services selected</p>
                    <p className="text-sm">Go to the Estimate tab and select services to include in the invoice.</p>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: "payments",
            label: "Payments",
            children: (
              <div className="p-8 text-center text-gray-500">
                Payment tracking coming soon...
              </div>
            ),
          },
          {
            key: "profit-loss",
            label: "Profit/Loss",
            children: <ProfitLoss />,
          },
        ]}
      />

      {/* Save Invoice Confirmation Modal */}
      <Modal
        title="Save Invoice"
        open={saveInvoiceModalVisible && savedInvoice === null}
        onOk={handleSaveInvoice}
        onCancel={() => setSaveInvoiceModalVisible(false)}
        okText="Save Invoice"
        cancelText="Cancel"
        okButtonProps={{ className: "bg-green-600 hover:bg-green-700" }}
        width={600}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to save this invoice? This will create a draft invoice that can be sent to QuickBooks or emailed to the client.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-semibold">
                  {formatCurrency(
                    (services || [])
                      .filter(service => selectedServices.has(service.id))
                      .reduce((total, service) => {
                        const cost = calculateServiceCost(service);
                        return total + cost.total;
                      }, 0) + (overallTotals?.adminChargesTotal || 0) + (overallTotals?.additionalChargesTotal || 0)
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Services:</span>
                <span>{selectedServices.size} service(s)</span>
              </div>
              {requestedDeposit > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Deposit:</span>
                  <span className="font-semibold">{formatCurrency(requestedDeposit)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Invoice Saved Success Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-600" size={24} />
            <span>Invoice Saved Successfully</span>
          </div>
        }
        open={saveInvoiceModalVisible && savedInvoice !== null}
        onCancel={() => {
          setSaveInvoiceModalVisible(false);
          setSavedInvoice(null);
        }}
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              onClick={() => {
                setSaveInvoiceModalVisible(false);
                setSavedInvoice(null);
              }}
            >
              Close
            </Button>
            <Button
              type="primary"
              onClick={() => {
                window.open(`/billing/invoicing`, '_blank');
                setSaveInvoiceModalVisible(false);
                setSavedInvoice(null);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Go to Invoicing
            </Button>
          </div>
        }
        width={600}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Your invoice has been saved successfully!
          </p>
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Invoice Number:</span>
                <span className="font-bold text-lg text-green-700">{savedInvoice?.invoiceNumber}</span>
              </div>
              <div className="text-sm text-gray-600 mt-2">
                This invoice is now saved as a draft and can be sent to QuickBooks or emailed to the client.
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            <strong>Next steps:</strong>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>View the invoice in the Invoicing tab</li>
              <li>Send to QuickBooks if connected</li>
              <li>Email the invoice to the client</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* Override Modal */}
      <PricingOverrideModal
        open={overrideModalOpen}
        onClose={closeOverrideModal}
        currentOverride={currentServiceOverride}
        onSave={saveOverride}
        onRemove={removeOverride}
        calculateServiceCost={calculateServiceCost}
      />

      {/* Hidden Invoice Print Content */}
      {invoiceData && (
        <InvoicePrintContent
          ref={invoicePrintRef}
          invoiceData={invoiceData}
          mode="invoice"
        />
      )}

      {/* Hidden Estimate Print Content */}
      {invoiceData && (
        <InvoicePrintContent
          ref={estimatePrintRef}
          invoiceData={invoiceData}
          mode="estimate"
        />
      )}

      {/* Hidden Print Content for Existing Invoices */}
      {invoiceToPrint && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <InvoicePrintContent
            ref={existingInvoicePrintRef}
            invoiceData={invoiceToPrint}
            mode="invoice"
          />
        </div>
      )}


      {/* Estimate Sidebar */}
      <EstimateSidebar
        booking={booking}
        invoiceData={invoiceData}
        versions={estimateVersions}
        currentTotal={overallTotals.total}
        onSave={handleSaveInvoiceConfig}
        onRestoreVersion={handleRestoreVersion}
        onDeleteVersion={handleDeleteVersion}
        onPrint={handleGenerateEstimate}
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSavingEstimate}
        currentServices={services}
        onSidebarToggle={setSidebarOpen}
      />
    </div>
  );
};

// Pricing Override Modal Component
const PricingOverrideModal: FC<{
  open: boolean;
  onClose: () => void;
  currentOverride: any;
  onSave: (override: ServiceOverride) => void;
  onRemove: (serviceId: string) => void;
  calculateServiceCost: (service: any) => any;
}> = ({ open, onClose, currentOverride, onSave, onRemove, calculateServiceCost }) => {
  const [override, setOverride] = useState<ServiceOverride | null>(null);

  // Update local state when currentOverride changes
  useEffect(() => {
    if (currentOverride) {
      setOverride({ ...currentOverride.override });
    } else {
      setOverride(null);
    }
  }, [currentOverride]);

  if (!currentOverride) return null;

  const service = currentOverride.service;
  const originalCalculation = calculateServiceCost(service);

  // Calculate with current overrides - create a temporary service with override applied
  const calculateWithOverride = () => {
    if (!override) return originalCalculation;

    // Apply custom variables to calculate new base cost
    const travelHours = originalCalculation.breakdown.travelHours || 0;
    const durationHours = originalCalculation.breakdown.durationHours || 0;
    const totalHours = override.customVariables?.hours || (travelHours + durationHours);
    const staffCount = override.customVariables?.staffCount || originalCalculation.breakdown.staffCount;
    const staffRate = override.customVariables?.staffRate || originalCalculation.breakdown.staffHourlyRate;
    const vesselRate = override.customVariables?.vesselRate || originalCalculation.breakdown.vesselHourlyRate;

    let calculatedTotal = 0;

    if (service.serviceType === ServiceType.COLLECTION || service.serviceType === ServiceType.DELIVERY) {
      const staffCost = staffCount * staffRate * totalHours;
      const vesselCost = vesselRate * totalHours;
      calculatedTotal = staffCost + vesselCost;
    } else if (service.serviceType === ServiceType.ON_SITE_SERVICE) {
      const staffCost = staffCount * staffRate * totalHours;
      const minimumCharge = 300 * staffCount;
      calculatedTotal = Math.max(staffCost, minimumCharge);
    } else if (service.serviceType === ServiceType.WAREHOUSE_SERVICE) {
      const cubicFeet = override.customVariables?.cubicFeet || 60;
      const minimumCharge = 75;
      calculatedTotal = Math.max(cubicFeet * 1, minimumCharge);
    } else if (service.serviceType === ServiceType.FABRICATION_SERVICE) {
      const staffCost = staffCount * (staffRate * 1.5) * totalHours;
      calculatedTotal = staffCost;
    } else {
      calculatedTotal = staffCount * staffRate * totalHours;
    }

    return calculatedTotal;
  };

  const recalculatedTotal = calculateWithOverride();

  const updateOverrideVariable = (key: string, value: number | string) => {
    if (!override) return;

    // Allow empty string during editing, only parse to number when value exists
    // For numeric fields, empty string means "use original", so we set to undefined
    // But we need to handle the case where 0 is a valid value (e.g., for staffCount)
    let numValue: number | undefined;
    if (typeof value === 'string') {
      if (value === '' || value === null || value === undefined) {
        numValue = undefined;
      } else {
        const parsed = parseFloat(value);
        numValue = isNaN(parsed) ? undefined : parsed;
      }
    } else {
      numValue = value;
    }

    const newOverride = {
      ...override,
      customVariables: {
        ...override.customVariables,
        [key]: numValue
      }
    };
    setOverride(newOverride);
  };

  const updateDiscount = (value: string | number) => {
    if (!override) return;

    // Allow empty string during editing
    const percentage = typeof value === 'string' 
      ? (value === '' ? undefined : (parseFloat(value) || 0))
      : value;

    setOverride({
      ...override,
      discountPercentage: percentage
    });
  };

  const updateCustomTotal = (value: string | number) => {
    if (!override) return;

    // Allow empty string during editing
    const total = typeof value === 'string' 
      ? (value === '' ? undefined : (parseFloat(value) || undefined))
      : value;

    setOverride({
      ...override,
      customTotal: total && total > 0 ? total : undefined
    });
  };

  const handleSave = () => {
    if (override) {
      onSave(override);
    }
  };

  const handleRemove = () => {
    onRemove(service.id);
  };

  return (
    <Modal
      title={`Override Pricing - ${capitalize(service.serviceType).replace(' Service', '')} #${service.serial}`}
      open={open}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="remove" onClick={handleRemove} danger>
          Remove Override
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save Override
        </Button>,
      ]}
    >
      {override && (
        <div className="space-y-6">
          {/* Original Calculation Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-3">Original Calculation Logic</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Service Type:</div>
                <div className="font-medium">{capitalize(service.serviceType)}</div>
              </div>
              <div>
                <div className="text-gray-600">Staff Count:</div>
                <div className="font-medium">{originalCalculation.breakdown.staffCount}</div>
              </div>
              <div>
                <div className="text-gray-600">Staff Rate:</div>
                <div className="font-medium">${originalCalculation.breakdown.staffHourlyRate}/hr</div>
              </div>
              <div>
                <div className="text-gray-600">Vehicle Rate:</div>
                <div className="font-medium">
                  {originalCalculation.breakdown.vesselHourlyRate > 0
                    ? `$${originalCalculation.breakdown.vesselHourlyRate}/hr`
                    : 'N/A'
                  }
                </div>
              </div>
              <div>
                <div className="text-gray-600">Total Hours:</div>
                <div className="font-medium">{originalCalculation.breakdown.totalHours.toFixed(2)}h</div>
              </div>
              <div>
                <div className="text-gray-600">Original Total:</div>
                <div className="font-bold text-lg">{formatCurrency(originalCalculation.baseTotal || originalCalculation.total)}</div>
              </div>
            </div>
          </div>

          {/* Variable Overrides */}
          <div>
            <h4 className="font-medium mb-3">Override Variables</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Staff Count
                </label>
                <Input
                  type="number"
                  min={0}
                  value={override.customVariables?.staffCount !== undefined ? override.customVariables.staffCount : originalCalculation.breakdown.staffCount}
                  onChange={(e) => updateOverrideVariable('staffCount', e.target.value)}
                  placeholder={originalCalculation.breakdown.staffCount.toString()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Staff Rate ($/hr)
                </label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={override.customVariables?.staffRate !== undefined ? override.customVariables.staffRate : originalCalculation.breakdown.staffHourlyRate}
                  onChange={(e) => updateOverrideVariable('staffRate', e.target.value)}
                  placeholder={originalCalculation.breakdown.staffHourlyRate.toString()}
                />
              </div>
              {originalCalculation.breakdown.vesselHourlyRate > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Rate ($/hr)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={override.customVariables?.vesselRate !== undefined ? override.customVariables.vesselRate : originalCalculation.breakdown.vesselHourlyRate}
                    onChange={(e) => updateOverrideVariable('vesselRate', e.target.value)}
                    placeholder={originalCalculation.breakdown.vesselHourlyRate.toString()}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Hours
                </label>
                <Input
                  type="number"
                  min={0}
                  step={0.25}
                  value={override.customVariables?.hours !== undefined ? override.customVariables.hours : originalCalculation.breakdown.totalHours}
                  onChange={(e) => updateOverrideVariable('hours', e.target.value)}
                  placeholder={originalCalculation.breakdown.totalHours.toString()}
                />
              </div>
              {service.serviceType === ServiceType.WAREHOUSE_SERVICE && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cubic Feet
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={0.1}
                    value={override.customVariables?.cubicFeet !== undefined ? override.customVariables.cubicFeet : 60}
                    onChange={(e) => updateOverrideVariable('cubicFeet', e.target.value)}
                    placeholder="60"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Percentage Discount */}
          <div>
            <h4 className="font-medium mb-3">Discount</h4>
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={override.discountPercentage !== undefined ? override.discountPercentage : ''}
                onChange={(e) => updateDiscount(e.target.value)}
                placeholder="0"
                className="w-24"
              />
              <span className="text-gray-600">% discount</span>
              {override.discountPercentage && override.discountPercentage > 0 && (
                <span className="text-green-600 font-medium">
                  (-{formatCurrency((originalCalculation.baseTotal || originalCalculation.total) * (override.discountPercentage / 100))})
                </span>
              )}
            </div>
          </div>

          {/* Custom Total Override */}
          <div>
            <h4 className="font-medium mb-3">Manual Total Override</h4>
            <div className="flex items-center space-x-2">
              <span className="text-gray-600">$</span>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={override.customTotal !== undefined ? override.customTotal : ''}
                onChange={(e) => updateCustomTotal(e.target.value)}
                placeholder="Leave blank to use calculated total"
                className="w-48"
              />
              <span className="text-gray-500 text-sm">(overrides all calculations)</span>
            </div>
          </div>

          {/* Calculated Total (before discount) */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Calculated Subtotal:</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatCurrency(recalculatedTotal)}
              </span>
            </div>
            {override.discountPercentage && override.discountPercentage > 0 && (
              <div className="flex justify-between items-center mt-2 text-sm">
                <span className="text-gray-600">Discount ({override.discountPercentage}%):</span>
                <span className="text-red-600">
                  -{formatCurrency(recalculatedTotal * (override.discountPercentage / 100))}
                </span>
              </div>
            )}
          </div>

          {/* Final Price Preview */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Final Price:</span>
              <span className="text-2xl font-bold text-blue-600">
                {formatCurrency(
                  override.customTotal !== undefined && override.customTotal > 0
                    ? override.customTotal
                    : recalculatedTotal - (override.discountPercentage ? (recalculatedTotal * (override.discountPercentage / 100)) : 0)
                )}
              </span>
            </div>
            {override.customTotal !== undefined && override.customTotal > 0 && (
              <div className="text-sm text-gray-600 mt-1">
                (Manual override - ignoring calculated values)
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};